import React, { useEffect, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Ban,
  CirclePause,
  CirclePlay,
  Clock3,
  ExternalLink,
  Hourglass,
  LockKeyhole,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { OdrLoadingSpinner } from '@/components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from '@/components/ui/toast';
import { useUser } from '@/store/useUser';

import {
  ALL_FILTER_VALUE,
  PAGE_SIZE,
  // PagerControls,
  SortDirection,
  SortableHeader,
  StatusIcon,
  TextCell,
  EcmFieldSettingsMenu,
  EcmExportButton,
  EcmResetButton,
  EcmRecordActionButton,
  EcmRecordDetailsDialog,
  buildMonthOptions,
  buildVisibleColumnSet,
  buildSearchText,
  calculateOutreachAttempts,
  compareValues,
  countServiceDatesForMonth,
  exportEcmDataset,
  formatDate,
  getCarePlanComplianceStatus,
  getCurrentMonthKey,
  getInitialAssessmentComplianceStatus,
  getMrunStatus,
  getNotesRequired,
  getProgramCompletionCompliance,
  getSelectedMonthLabel,
  getDaysDifference,
  matchesFilter,
  isInteractiveRecordTarget,
  normalizeLowerCase,
  normalizeValue,
  parseMonthKey,
  resolveVisibleColumnIds,
  uniqueSortedValues,
  FilterLabel,
  MissingValue,
  EcmAddParticipantDialog,
  ECM_ADD_OUTREACH_PARTICIPANT_MUTATION,
  ECM_MOVE_CENSUS_PARTICIPANT_TO_OUTREACH_MUTATION,
  ECM_MOVE_OUTREACH_PARTICIPANT_MUTATION,
  ECM_REMOVE_CENSUS_PARTICIPANT_MUTATION,
  ECM_REMOVE_OUTREACH_PARTICIPANT_MUTATION,
  ECM_UPDATE_DETERMINATION_MUTATION,
  type EcmFieldOption,
} from './EcmDashboardShared';

export const EXCEPTIONS_QUERY = gql`
  query EcmExceptions($month: Int!, $year: Int!) {
    ecmExceptions(month: $month, year: $year) {
      dateRangeEnd
      dateRangeStart
      items {
        address
        censusStart
        cid
        cin
        determination
        dob
        facility
        firstName
        healthPlan
        lastName
        lcm
        notes
        odrparticipant
        participantId
        participantIds
        phoneNumber
        program
        programs
        qa
      }
    }
  }
`;

export const DOCUMENTATION_QUERY = gql`
  query EcmDocumentation($month: Int!, $year: Int!) {
    ecmDocumentation(month: $month, year: $year) {
      dateRangeEnd
      dateRangeStart
      items {
        acuity
        authorizationEndDate
        carePlanDate
        censusStart
        cid
        cin
        determination
        dob
        facility
        firstName
        healthPlan
        initialAssessmentDate
        lastName
        lcm
        odrparticipant
        participantId
        participantIds
        program
        programCompletionDate
        programs
        qa
        serviceDates
      }
    }
  }
`;

export const CALAIM_QUERY = gql`
  query EcmCalaimDemographics($month: Int!, $year: Int!) {
    ecmCalaimDemographics(month: $month, year: $year) {
      dateRangeEnd
      dateRangeStart
      items {
        cid
        cin
        clinicEmpaneled
        dhsEmpaneled
        dob
        ecmEffectiveDate
        ecmProvider
        facility
        fin
        firstName
        healthPlan
        lastName
        lcm
        mrun
        odrparticipant
        participantId
        participantIds
        pcpName
        program
        programs
        qa
        ssn
      }
    }
  }
`;

export const OUTREACH_QUERY = gql`
  query EcmOutreach {
    ecmOutreach {
      items {
        cid
        cin
        dob
        firstName
        healthPlan
        lastName
        notes
        odrparticipant
        outreachAttempts {
          outreachDate
          outreachResult
        }
        outreachEnd
        outreachStart
        outreachStatus
        participantId
        participantIds
        program
        programs
        qa
      }
    }
  }
`;

export const DISCHARGE_QUERY = gql`
  query EcmDischarge($mode: String!) {
    ecmDischarge(mode: $mode) {
      items {
        address
        cid
        cin
        determination
        dob
        endDate
        enteredDate
        facility
        firstName
        healthPlan
        lastName
        lcm
        notes
        odrparticipant
        participantId
        participantIds
        phoneNumber
        program
        programs
        qa
      }
    }
  }
`;

export const PROVIDER_MEDICAL_QUERY = gql`
  query EcmProviderMedicalDemographics {
    ecmProviderMedicalDemographics {
      items {
        cid
        cin
        dob
        facility
        fin
        firstName
        healthPlan
        lastName
        lcm
        mrun
        notes
        odrparticipant
        participantId
        participantIds
        programs
        qa
        ssn
      }
    }
  }
`;

const MIF_QUERY = gql`
  query EcmMifTools($mifId: Int) {
    ecmMifTools(mifId: $mifId) {
      items {
        cid
        cin
        courtConsent
        disposition
        discontinuationReason
        dob
        ecmCareManager
        ecmEffectiveDate
        ecmEnrollmentEndDate
        ecmEnrollmentStartDate
        ecmOptIn
        ecmProvider
        ecmRecord
        ecmReferralDate
        empaneledToClinic
        empaneledToDhs
        empanelmentEffectiveDate
        empanelmentRequestedDate
        fin
        firstName
        gender
        healthPlan
        lastName
        mrun
        odrparticipant
        participantId
        pcpName
        program
        ssn
        wpcGrandfathered
      }
      selectedUploadId
      uploads {
        createdAt
        id
        title
      }
    }
  }
`;

const CENSUS_LOCK_QUERY = gql`
  query EcmCensusLock($month: Int!, $year: Int!) {
    ecmCensusLock(month: $month, year: $year) {
      dateRangeEnd
      dateRangeStart
      items {
        address
        authorizationEndDate
        censusStart
        cid
        cin
        determination
        dob
        facility
        firstName
        healthPlan
        lastName
        lcm
        lockSource
        lockedAt
        notes
        odrparticipant
        participantId
        participantIds
        phoneNumber
        program
        programs
        qa
      }
      lockDate
      lockedCount
      snapshotId
      viewMode
    }
  }
`;

const LOCK_CENSUS_MONTH_MUTATION = gql`
  mutation EcmLockCensusMonth($month: Int!, $year: Int!) {
    ecmLockCensusMonth(month: $month, year: $year) {
      alreadyLocked
      censusRowsMarked
      lockDate
      message
      participantsLocked
      snapshotId
      success
      viewMode
    }
  }
`;

const PanelFrame = ({
  children,
  error,
  summary,
  toolbar,
}: {
  children: React.ReactNode;
  error?: string | null;
  summary: React.ReactNode;
  toolbar: React.ReactNode;
}) => {
  return (
    <div className="space-y-3">
      {error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{error}</div>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-3">{toolbar}</div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-2 text-xs text-slate-600">
          {summary}
        </div>
        {children}
      </div>
    </div>
  );
};

const MonthSelectControl = ({
  label = 'Month',
  monthOptions,
  selectedMonthKey,
  selectedMonthLabel,
  onChange,
}: {
  label?: string;
  monthOptions: Array<{ label: string; value: string }>;
  onChange: (value: string) => void;
  selectedMonthKey: string;
  selectedMonthLabel: string;
}) => {
  return (
    <div className="space-y-1">
      <FilterLabel>{label}</FilterLabel>
      <Select value={selectedMonthKey} onValueChange={onChange}>
        <SelectTrigger className="h-8 w-[190px] text-xs">
          <SelectValue aria-label={label} />
        </SelectTrigger>
        <SelectContent>
          {monthOptions.length === 0 ? <SelectItem value={selectedMonthKey}>{selectedMonthLabel}</SelectItem> : null}
          {monthOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

const PARTICIPANT_LINK_CLASS_NAME = 'text-sky-700 hover:text-sky-900 hover:underline';

export const toParticipantPath = (item: any) => {
  const odrparticipant = Number(item?.odrparticipant);
  if (!Number.isInteger(odrparticipant) || odrparticipant < 1) {
    return null;
  }

  return `/participants/${odrparticipant}`;
};

const ParticipantProfileCellValue = ({
  fallback = '—',
  item,
  value,
}: {
  fallback?: React.ReactNode;
  item: any;
  value: unknown;
}) => {
  const normalizedText = normalizeValue(value);
  if (!normalizedText) {
    return <>{fallback}</>;
  }

  const participantPath = toParticipantPath(item);
  if (!participantPath) {
    return <>{normalizedText}</>;
  }

  return (
    <RouterLink className={PARTICIPANT_LINK_CLASS_NAME} to={participantPath}>
      {normalizedText}
    </RouterLink>
  );
};

const getParticipantRecordLabel = (item: any) => {
  return [normalizeValue(item?.firstName), normalizeValue(item?.lastName)].filter(Boolean).join(' ') || 'participant';
};

const DocumentationStatusCell = ({
  linkLabel,
  linkTo,
  status,
  value,
}: {
  linkLabel: string;
  linkTo: string;
  status: boolean | string | null | undefined;
  value: string | null | undefined;
}) => {
  return (
    <div className="flex min-w-[8rem] items-start gap-2">
      <div className="pt-0.5">
        <StatusIcon status={status} />
      </div>
      <div className="space-y-1">
        <div className="text-xs text-slate-700">{normalizeValue(value) ? formatDate(value) : '—'}</div>
        <RouterLink className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:text-sky-900" to={linkTo}>
          {linkLabel}
          <ExternalLink className="h-3 w-3" />
        </RouterLink>
      </div>
    </div>
  );
};

const AttemptCell = ({ attempt }: { attempt: { nextAttemptDate?: string | null; status?: string | null } }) => {
  const title = normalizeValue(attempt?.nextAttemptDate) ? formatDate(attempt.nextAttemptDate) : '';
  return (
    <div className="flex items-center gap-2">
      <StatusIcon status={attempt?.status} />
      <span className="text-xs text-slate-600">{title || '—'}</span>
    </div>
  );
};

const formatOutreachAttemptForExport = (attempt: {
  attemptDate?: string | null;
  nextAttemptDate?: string | null;
  result?: number | null;
  status?: string | null;
}) => {
  return [
    normalizeValue(attempt?.attemptDate) ? `Attempt: ${formatDate(attempt.attemptDate)}` : '',
    normalizeValue(attempt?.nextAttemptDate) ? `Next: ${formatDate(attempt.nextAttemptDate)}` : '',
    normalizeValue(attempt?.status) ? `Status: ${attempt.status}` : '',
  ].filter(Boolean).join(' | ');
};

const getActionErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (error instanceof Error && normalizeValue(error.message)) {
    return error.message;
  }

  return fallbackMessage;
};

type ExceptionsSortField =
  | 'censusStart'
  | 'cid'
  | 'cin'
  | 'determination'
  | 'dob'
  | 'facility'
  | 'firstName'
  | 'healthPlan'
  | 'lastName'
  | 'lcm'
  | 'program'
  | 'qa';

const getExceptionSortValue = (item: any, field: ExceptionsSortField) => {
  if (field === 'censusStart') {
    return normalizeValue(item.censusStart);
  }

  if (field === 'cid') {
    return normalizeValue(item.cid);
  }

  if (field === 'cin') {
    return normalizeValue(item.cin);
  }

  if (field === 'determination') {
    return normalizeValue(item.determination);
  }

  if (field === 'dob') {
    return normalizeValue(item.dob);
  }

  if (field === 'facility') {
    return normalizeValue(item.facility);
  }

  if (field === 'firstName') {
    return normalizeValue(item.firstName);
  }

  if (field === 'healthPlan') {
    return normalizeValue(item.healthPlan);
  }

  if (field === 'lcm') {
    return normalizeValue(item.lcm);
  }

  if (field === 'program') {
    return normalizeValue(item.program);
  }

  if (field === 'qa') {
    return normalizeValue(item.qa);
  }

  return normalizeValue(item.lastName);
};

const ECM_EXCEPTIONS_COLUMNS: EcmFieldOption[] = [
  { id: 'firstName', label: 'First Name' },
  { id: 'lastName', label: 'Last Name' },
  { id: 'program', label: 'Program' },
  { id: 'censusStart', label: 'Date Entered' },
  { id: 'address', label: 'Address' },
  { id: 'phoneNumber', label: 'Phone #' },
  { id: 'notes', label: 'Notes' },
  { id: 'cid', label: 'CID' },
  { id: 'cin', label: 'CIN' },
  { id: 'healthPlan', label: 'Health Plan Provider' },
  { id: 'lcm', label: 'LCM' },
  { id: 'dob', label: 'DOB' },
  { id: 'qa', label: 'QA' },
  { id: 'determination', label: 'Determination' },
];

const ECM_CALAIM_COLUMNS: EcmFieldOption[] = [
  { id: 'firstName', label: 'First Name' },
  { id: 'lastName', label: 'Last Name' },
  { id: 'program', label: 'Program' },
  { id: 'cid', label: 'CID' },
  { id: 'ssn', label: 'SSN' },
  { id: 'cin', label: 'MediCal CIN' },
  { id: 'dob', label: 'DOB' },
  { id: 'mrun', label: 'MRUN' },
  { id: 'fin', label: 'FIN' },
  { id: 'ecmEffectiveDate', label: 'ECM Effective Date' },
  { id: 'healthPlan', label: 'Health Plan Provider' },
  { id: 'dhsEmpaneled', label: 'Empaneled to DHS' },
  { id: 'clinicEmpaneled', label: 'Empaneled to Clinic' },
  { id: 'pcpName', label: 'PCP Name' },
  { id: 'qa', label: 'QA' },
];

const ECM_OUTREACH_COLUMNS: EcmFieldOption[] = [
  { id: 'firstName', label: 'First Name' },
  { id: 'lastName', label: 'Last Name' },
  { id: 'program', label: 'Program' },
  { id: 'outreachStart', label: 'Outreach Start' },
  { id: 'outreachEnd', label: 'Outreach End' },
  { id: 'cid', label: 'CID' },
  { id: 'cin', label: 'CIN' },
  { id: 'dob', label: 'DOB' },
  { id: 'healthPlan', label: 'Health Plan Provider' },
  { id: 'notes', label: 'Notes' },
  { id: 'attempt1', label: 'Attempt 1' },
  { id: 'attempt2', label: 'Attempt 2' },
  { id: 'attempt3', label: 'Attempt 3' },
  { id: 'nextStep', label: 'Next Step' },
  { id: 'qa', label: 'QA' },
];

const ECM_DISCHARGE_COLUMNS: EcmFieldOption[] = [
  { id: 'firstName', label: 'First Name' },
  { id: 'lastName', label: 'Last Name' },
  { id: 'program', label: 'Program' },
  { id: 'enteredDate', label: 'Date Entered' },
  { id: 'endDate', label: 'Date Ended' },
  { id: 'totalDays', label: 'Total Days' },
  { id: 'address', label: 'Address' },
  { id: 'phoneNumber', label: 'Phone #' },
  { id: 'notes', label: 'Notes' },
  { id: 'cid', label: 'CID' },
  { id: 'cin', label: 'CIN' },
  { id: 'healthPlan', label: 'Health Plan Provider' },
  { id: 'lcm', label: 'LCM' },
  { id: 'dob', label: 'DOB' },
  { id: 'qa', label: 'QA' },
];

const ECM_PROVIDER_MEDICAL_COLUMNS: EcmFieldOption[] = [
  { id: 'firstName', label: 'First Name' },
  { id: 'lastName', label: 'Last Name' },
  { id: 'odrparticipant', label: 'ODR Participant Number' },
  { id: 'notes', label: 'Notes' },
  { id: 'cid', label: 'CID' },
  { id: 'ssn', label: 'SSN' },
  { id: 'cin', label: 'MediCal CIN' },
  { id: 'dob', label: 'DOB' },
  { id: 'mrun', label: 'MRUN' },
  { id: 'fin', label: 'FIN' },
  { id: 'healthPlan', label: 'Health Plan Provider' },
  { id: 'qa', label: 'QA' },
  { id: 'lcm', label: 'LCM' },
];

const ECM_MIF_COLUMNS: EcmFieldOption[] = [
  { id: 'odrparticipant', label: 'ODR Participant' },
  { id: 'cin', label: 'CIN' },
  { id: 'healthPlan', label: 'Health Plan Provider' },
  { id: 'cid', label: 'CID' },
  { id: 'firstName', label: 'First Name' },
  { id: 'lastName', label: 'Last Name' },
  { id: 'dob', label: 'DOB' },
  { id: 'gender', label: 'Gender' },
  { id: 'ssn', label: 'SSN' },
  { id: 'mrun', label: 'MRUN' },
  { id: 'fin', label: 'FIN' },
  { id: 'wpcGrandfathered', label: 'WPC Grandfathered' },
  { id: 'ecmCareManager', label: 'ECM Care Manager' },
  { id: 'ecmOptIn', label: 'ECM opt-in' },
  { id: 'ecmRecord', label: 'ECM Record' },
  { id: 'ecmEnrollmentStartDate', label: 'ECM Enrollment Start Date' },
  { id: 'ecmEnrollmentEndDate', label: 'ECM Enrollment End Date' },
  { id: 'ecmEffectiveDate', label: 'ECM Effective Date' },
  { id: 'ecmReferralDate', label: 'ECM Referral Date' },
  { id: 'discontinuationReason', label: 'Discontinuation Reason Code' },
  { id: 'empanelmentEffectiveDate', label: 'Empanelment Effective Date' },
  { id: 'empanelmentRequestedDate', label: 'Empanelment Requested Date' },
  { id: 'empaneledToDhs', label: 'Empaneled to DHS' },
  { id: 'empaneledToClinic', label: 'Empaneled to Clinic' },
  { id: 'pcpName', label: 'PCP Name' },
  { id: 'courtConsent', label: 'Court Consent' },
  { id: 'disposition', label: 'Disposition' },
];

const ECM_CENSUS_LOCK_COLUMNS: EcmFieldOption[] = [
  { id: 'firstName', label: 'First Name' },
  { id: 'lastName', label: 'Last Name' },
  { id: 'program', label: 'Program' },
  { id: 'censusStart', label: 'Date Entered' },
  { id: 'authorizationEndDate', label: 'Auth End Date' },
  { id: 'address', label: 'Address' },
  { id: 'phoneNumber', label: 'Phone #' },
  { id: 'notes', label: 'Notes' },
  { id: 'cid', label: 'CID' },
  { id: 'cin', label: 'CIN' },
  { id: 'healthPlan', label: 'Health Plan Provider' },
  { id: 'lcm', label: 'LCM' },
  { id: 'dob', label: 'DOB' },
  { id: 'qa', label: 'QA' },
  { id: 'status', label: 'Status' },
];

const useEcmFieldSettings = (pageKey: string, columns: EcmFieldOption[]) => {
  const { ecmDashboardColumnPreferenceMap, updateEcmDashboardColumnPreferences } = useUser();
  const visibleColumnIds = resolveVisibleColumnIds(ecmDashboardColumnPreferenceMap?.[pageKey], columns.map((column) => column.id));
  const visibleColumnSet = buildVisibleColumnSet(visibleColumnIds);

  return {
    fieldSettingsControl: (
      <EcmFieldSettingsMenu
        columns={columns}
        visibleColumnIds={visibleColumnIds}
        onVisibleColumnsChange={(nextVisibleColumnIds) => {
          void updateEcmDashboardColumnPreferences(pageKey, nextVisibleColumnIds);
        }}
      />
    ),
    visibleColumnCount: visibleColumnIds.length,
    visibleColumnSet,
  };
};

export const EcmDashboardExceptionsView = ({
  canManageParticipants = false,
}: {
  canManageParticipants?: boolean;
}) => {
  const [searchText, setSearchText] = useState('');
  const [programFilter, setProgramFilter] = useState(ALL_FILTER_VALUE);
  const [facilityFilter, setFacilityFilter] = useState(ALL_FILTER_VALUE);
  const [qaFilter, setQaFilter] = useState(ALL_FILTER_VALUE);
  const [lcmFilter, setLcmFilter] = useState(ALL_FILTER_VALUE);
  const [determinationFilter, setDeterminationFilter] = useState(ALL_FILTER_VALUE);
  const [sortField, setSortField] = useState<ExceptionsSortField>('lastName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(1);
  const [selectedMonthKey, setSelectedMonthKey] = useState(getCurrentMonthKey);
  const [activeActionKey, setActiveActionKey] = useState<string | null>(null);
  const [selectedDetailsItem, setSelectedDetailsItem] = useState<any | null>(null);
  const { fieldSettingsControl, visibleColumnCount, visibleColumnSet } = useEcmFieldSettings('exceptions', ECM_EXCEPTIONS_COLUMNS);

  const selectedMonth = parseMonthKey(selectedMonthKey);
  const { data, error, loading, refetch } = useQuery(EXCEPTIONS_QUERY, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: selectedMonth,
  });
  const [moveCensusParticipantToOutreach] = useMutation(ECM_MOVE_CENSUS_PARTICIPANT_TO_OUTREACH_MUTATION);
  const [removeCensusParticipant] = useMutation(ECM_REMOVE_CENSUS_PARTICIPANT_MUTATION);
  const [updateDetermination] = useMutation(ECM_UPDATE_DETERMINATION_MUTATION);

  const result = data?.ecmExceptions;
  const monthOptions = buildMonthOptions(result?.dateRangeStart, result?.dateRangeEnd);
  const selectedMonthLabel = getSelectedMonthLabel(selectedMonthKey, monthOptions);

  useEffect(() => {
    if (monthOptions.length === 0) {
      return;
    }

    const hasSelectedMonth = monthOptions.some((option) => option.value === selectedMonthKey);
    if (hasSelectedMonth) {
      return;
    }

    setSelectedMonthKey(monthOptions[0].value);
    setPage(1);
  }, [monthOptions, selectedMonthKey]);

  const items = Array.isArray(result?.items) ? result.items : [];
  const programs = uniqueSortedValues(items.map((item: any) => item.program));
  const facilities = uniqueSortedValues(items.map((item: any) => item.facility));
  const qas = uniqueSortedValues(items.map((item: any) => item.qa));
  const lcms = uniqueSortedValues(items.map((item: any) => item.lcm));
  const determinations = uniqueSortedValues(items.map((item: any) => item.determination));

  let filteredItems = items.filter((item: any) => {
    if (!matchesFilter(item.program, programFilter)) {
      return false;
    }

    if (!matchesFilter(item.facility, facilityFilter)) {
      return false;
    }

    if (!matchesFilter(item.qa, qaFilter)) {
      return false;
    }

    if (!matchesFilter(item.lcm, lcmFilter)) {
      return false;
    }

    if (!matchesFilter(item.determination, determinationFilter)) {
      return false;
    }

    const normalizedSearchText = normalizeLowerCase(searchText);
    if (!normalizedSearchText) {
      return true;
    }

    return buildSearchText([
      item.address,
      item.cid,
      item.cin,
      item.determination,
      item.facility,
      item.firstName,
      item.healthPlan,
      item.lastName,
      item.lcm,
      item.notes,
      item.phoneNumber,
      item.program,
      item.qa,
    ]).includes(normalizedSearchText);
  });

  filteredItems = [...filteredItems].sort((left: any, right: any) => {
    const sortValueComparison = compareValues(getExceptionSortValue(left, sortField), getExceptionSortValue(right, sortField), sortDirection);
    if (sortValueComparison !== 0) {
      return sortValueComparison;
    }

    const lastNameComparison = compareValues(normalizeValue(left.lastName), normalizeValue(right.lastName), 'asc');
    if (lastNameComparison !== 0) {
      return lastNameComparison;
    }

    return compareValues(normalizeValue(left.firstName), normalizeValue(right.firstName), 'asc');
  });

  const totalCount = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    if (page <= totalPages) {
      return;
    }

    setPage(totalPages);
  }, [page, totalPages]);

  // Pagination is temporarily disabled.
  // const visibleItems = filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const visibleItems = filteredItems;
  const tableColumnCount = visibleColumnCount;

  const handleSort = (field: string) => {
    const normalizedField = field as ExceptionsSortField;
    if (sortField === normalizedField) {
      setSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortField(normalizedField);
    setSortDirection('asc');
  };

  const resetFilters = () => {
    setSearchText('');
    setProgramFilter(ALL_FILTER_VALUE);
    setFacilityFilter(ALL_FILTER_VALUE);
    setQaFilter(ALL_FILTER_VALUE);
    setLcmFilter(ALL_FILTER_VALUE);
    setDeterminationFilter(ALL_FILTER_VALUE);
    setSortField('lastName');
    setSortDirection('asc');
    setPage(1);
  };

  const runAction = async ({
    action,
    actionKey,
    errorMessage,
    successMessage,
  }: {
    action: () => Promise<void>;
    actionKey: string;
    errorMessage: string;
    successMessage: string;
  }) => {
    setActiveActionKey(actionKey);
    try {
      await action();
      toast.success(successMessage);
      await refetch();
    } catch (error) {
      toast.error(getActionErrorMessage(error, errorMessage));
    } finally {
      setActiveActionKey(null);
    }
  };

  const updateParticipantDetermination = async (item: any, determination: string, label: string) => {
    const participantName = [normalizeValue(item.firstName), normalizeValue(item.lastName)].filter(Boolean).join(' ') || 'this participant';
    const actionConfirmed = window.confirm(`Set ${participantName} determination to ${label}?`);
    if (!actionConfirmed) {
      return;
    }

    await runAction({
      action: async () => {
        const response = await updateDetermination({
          variables: { determination, odrparticipant: item.odrparticipant },
        });
        if (!response.data?.updateEcmDetermination?.success) {
          throw new Error(`Unable to set participant determination to ${label}.`);
        }
      },
      actionKey: `determination-${determination}-${item.odrparticipant}`,
      errorMessage: `Unable to set participant determination to ${label}.`,
      successMessage: `Participant determination set to ${label}.`,
    });
  };

  const removeParticipantFromCensus = async (item: any) => {
    const participantName = [normalizeValue(item.firstName), normalizeValue(item.lastName)].filter(Boolean).join(' ') || 'this participant';
    const participantCin = normalizeValue(item.cin);
    const removalConfirmed = window.confirm(
      `Remove ${participantName}${participantCin ? ` (CIN ${participantCin})` : ''} from Census?`
    );
    if (!removalConfirmed) {
      return;
    }

    await runAction({
      action: async () => {
        const response = await removeCensusParticipant({ variables: { odrparticipant: item.odrparticipant } });
        if (!response.data?.removeEcmCensusParticipant?.success) {
          throw new Error('Unable to remove participant from Census.');
        }
      },
      actionKey: `remove-${item.odrparticipant}`,
      errorMessage: 'Unable to remove participant from Census.',
      successMessage: 'Participant removed from Census.',
    });
  };

  const addParticipantToOutreach = async (item: any) => {
    const participantName = [normalizeValue(item.firstName), normalizeValue(item.lastName)].filter(Boolean).join(' ') || 'this participant';
    const participantCin = normalizeValue(item.cin);
    const addConfirmed = window.confirm(
      `Move ${participantName}${participantCin ? ` (CIN ${participantCin})` : ''} from Exceptions to Outreach?`
    );
    if (!addConfirmed) {
      return;
    }

    await runAction({
      action: async () => {
        const response = await moveCensusParticipantToOutreach({ variables: { odrparticipant: item.odrparticipant } });
        if (!response.data?.moveEcmCensusParticipantToOutreach?.success) {
          throw new Error('Unable to move participant to Outreach.');
        }
      },
      actionKey: `add-to-outreach-${item.odrparticipant}`,
      errorMessage: 'Unable to move participant to Outreach.',
      successMessage: 'Participant moved to Outreach.',
    });
  };

  if (loading && !data) {
    return <OdrLoadingSpinner message="Loading ECM Exceptions" />;
  }

  if (error && !result) {
    return <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">Unable to load ECM Exceptions: {error.message}</div>;
  }

  const selectedActionInProgress = Boolean(
    selectedDetailsItem && (activeActionKey || '').endsWith(`-${selectedDetailsItem.odrparticipant}`)
  );

  return (
    <>
    <PanelFrame
      error={error ? `Showing saved results while the latest refresh failed: ${error.message}` : null}
      summary={
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-slate-800">{selectedMonthLabel}</span>
            <span>•</span>
            <span>{totalCount} matching participants</span>
            <span>•</span>
            <span>{items.length} total for month</span>
          </div>
          {/* <PagerControls page={page} totalCount={totalCount} totalPages={totalPages} onPageChange={setPage} /> */}
        </>
      }
      toolbar={
        <div className="flex flex-wrap items-end gap-3">
          <MonthSelectControl
            monthOptions={monthOptions}
            selectedMonthKey={selectedMonthKey}
            selectedMonthLabel={selectedMonthLabel}
            onChange={(value) => {
              setSelectedMonthKey(value);
              setPage(1);
            }}
          />
          <div className="space-y-1">
            <FilterLabel>Search</FilterLabel>
            <Input
              className="h-8 w-[240px] text-xs"
              placeholder="Name, CID, CIN, notes"
              value={searchText}
              onChange={(event) => {
                setSearchText(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <SimpleSelect label="Program" value={programFilter} options={programs} allLabel="All Programs" onChange={setProgramFilter} />
          <SimpleSelect label="Facility" value={facilityFilter} options={facilities} allLabel="All Facilities" onChange={setFacilityFilter} />
          <SimpleSelect label="QA" value={qaFilter} options={qas} allLabel="All QA" onChange={setQaFilter} />
          <SimpleSelect label="LCM" value={lcmFilter} options={lcms} allLabel="All LCM" onChange={setLcmFilter} />
          <SimpleSelect
            label="Determination"
            value={determinationFilter}
            options={determinations}
            allLabel="All Determinations"
            onChange={setDeterminationFilter}
          />
          <EcmResetButton onReset={resetFilters} />
          <div className="ml-auto flex items-center gap-2">
            <EcmExportButton
              onExport={() => {
                exportEcmDataset({
                  columns: ECM_EXCEPTIONS_COLUMNS,
                  fileName: `ecm-exceptions-${selectedMonthKey}`,
                  rows: items,
                });
              }}
            />
            {fieldSettingsControl}
          </div>
        </div>
      }
    >
      <Table className="min-w-[1700px]">
        <TableHeader className="bg-slate-50">
          <TableRow className="hover:bg-slate-50">
            {visibleColumnSet.has('firstName') ? <TableHead><SortableHeader field="firstName" label="First Name" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('lastName') ? <TableHead><SortableHeader field="lastName" label="Last Name" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('program') ? <TableHead><SortableHeader field="program" label="Program" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('censusStart') ? <TableHead><SortableHeader field="censusStart" label="Date Entered" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('address') ? <TableHead>Address</TableHead> : null}
            {visibleColumnSet.has('phoneNumber') ? <TableHead>Phone #</TableHead> : null}
            {visibleColumnSet.has('notes') ? <TableHead>Notes</TableHead> : null}
            {visibleColumnSet.has('cid') ? <TableHead><SortableHeader field="cid" label="CID" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('cin') ? <TableHead><SortableHeader field="cin" label="CIN" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('healthPlan') ? <TableHead><SortableHeader field="healthPlan" label="Health Plan Provider" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('facility') ? <TableHead><SortableHeader field="facility" label="Facility" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('lcm') ? <TableHead><SortableHeader field="lcm" label="LCM" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('dob') ? <TableHead><SortableHeader field="dob" label="DOB" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('qa') ? <TableHead><SortableHeader field="qa" label="QA" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('determination') ? <TableHead><SortableHeader field="determination" label="Determination" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleItems.length === 0 ? (
            <TableRow className="hover:bg-white">
              <TableCell className="py-10 text-center text-sm text-slate-500" colSpan={tableColumnCount}>
                No participants match the current filters.
              </TableCell>
            </TableRow>
          ) : null}
          {visibleItems.map((item: any) => {
            const recordLabel = getParticipantRecordLabel(item);

            return (
              <TableRow
                aria-haspopup="dialog"
                aria-label={`View Exceptions details for ${recordLabel}`}
                className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--azure-focus-ring)]"
                key={`${item.odrparticipant}-${item.participantId || 'none'}`}
                tabIndex={0}
                onClick={(event) => {
                  if (!isInteractiveRecordTarget(event.target)) {
                    setSelectedDetailsItem(item);
                  }
                }}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) {
                    return;
                  }
                  event.preventDefault();
                  setSelectedDetailsItem(item);
                }}
              >
                {visibleColumnSet.has('firstName') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.firstName) || '—'}</TableCell> : null}
                {visibleColumnSet.has('lastName') ? <TableCell className="whitespace-nowrap font-medium text-slate-900">{normalizeValue(item.lastName) || '—'}</TableCell> : null}
                {visibleColumnSet.has('program') ? (
                  <TableCell className="whitespace-nowrap"><ParticipantProfileCellValue item={item} value={item.program} /></TableCell>
                ) : null}
                {visibleColumnSet.has('censusStart') ? <TableCell className="whitespace-nowrap">{formatDate(item.censusStart)}</TableCell> : null}
                {visibleColumnSet.has('address') ? <TableCell className="max-w-[14rem]"><TextCell value={item.address} /></TableCell> : null}
                {visibleColumnSet.has('phoneNumber') ? <TableCell className="whitespace-nowrap"><TextCell value={item.phoneNumber} /></TableCell> : null}
                {visibleColumnSet.has('notes') ? <TableCell className="max-w-[14rem]"><TextCell value={item.notes} /></TableCell> : null}
                {visibleColumnSet.has('cid') ? <TableCell className="whitespace-nowrap"><ParticipantProfileCellValue item={item} value={item.cid} /></TableCell> : null}
                {visibleColumnSet.has('cin') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.cin) || '—'}</TableCell> : null}
                {visibleColumnSet.has('healthPlan') ? <TableCell className="max-w-[12rem]"><TextCell value={item.healthPlan} /></TableCell> : null}
                {visibleColumnSet.has('facility') ? <TableCell className="max-w-[12rem]"><TextCell value={item.facility} /></TableCell> : null}
                {visibleColumnSet.has('lcm') ? <TableCell className="max-w-[12rem]"><TextCell value={item.lcm} /></TableCell> : null}
                {visibleColumnSet.has('dob') ? <TableCell className="whitespace-nowrap">{formatDate(item.dob)}</TableCell> : null}
                {visibleColumnSet.has('qa') ? <TableCell className="max-w-[12rem]"><TextCell value={item.qa} /></TableCell> : null}
                {visibleColumnSet.has('determination') ? <TableCell className="whitespace-nowrap capitalize">{normalizeValue(item.determination).replaceAll('_', ' ') || '—'}</TableCell> : null}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </PanelFrame>
    <EcmRecordDetailsDialog
      actions={selectedDetailsItem ? (
        <>
          <EcmRecordActionButton disabled={selectedActionInProgress} icon={<Ban className="h-4 w-4" />} label="Terminated" onClick={() => { void updateParticipantDetermination(selectedDetailsItem, 'terminated', 'Terminated'); }} />
          <EcmRecordActionButton disabled={selectedActionInProgress} icon={<Clock3 className="h-4 w-4" />} label="Pending" onClick={() => { void updateParticipantDetermination(selectedDetailsItem, 'pending', 'Pending'); }} />
          <EcmRecordActionButton disabled={selectedActionInProgress} icon={<CirclePlay className="h-4 w-4" />} label="Continued" onClick={() => { void updateParticipantDetermination(selectedDetailsItem, 'continued', 'Continued'); }} />
          <EcmRecordActionButton disabled={selectedActionInProgress} icon={<CirclePause className="h-4 w-4" />} label="On Hold" onClick={() => { void updateParticipantDetermination(selectedDetailsItem, 'on_hold', 'On Hold'); }} />
          <EcmRecordActionButton disabled={selectedActionInProgress} icon={<Hourglass className="h-4 w-4" />} label="Pending Termination" onClick={() => { void updateParticipantDetermination(selectedDetailsItem, 'pending termination', 'Pending Termination'); }} />
          <EcmRecordActionButton disabled={selectedActionInProgress} icon={<Trash2 className="h-4 w-4" />} label="Remove from Census" separatorBefore variant="destructive" onClick={() => { void removeParticipantFromCensus(selectedDetailsItem); }} />
          <EcmRecordActionButton disabled={selectedActionInProgress} icon={<UserPlus className="h-4 w-4" />} label="Add to Outreach" onClick={() => { void addParticipantToOutreach(selectedDetailsItem); }} />
        </>
      ) : null}
      columns={ECM_EXCEPTIONS_COLUMNS}
      item={selectedDetailsItem}
      onOpenChange={(open) => {
        if (!open) {
          setSelectedDetailsItem(null);
        }
      }}
      title="Exceptions Details"
    />
    </>
  );
};

const SimpleSelect = ({
  allLabel,
  label,
  onChange,
  options,
  value,
  widthClassName = 'w-[170px]',
}: {
  allLabel: string;
  label: string;
  onChange: (value: string) => void;
  options: string[];
  value: string;
  widthClassName?: string;
}) => {
  return (
    <div className="space-y-1">
      <FilterLabel>{label}</FilterLabel>
      <Select
        value={value}
        onValueChange={(selectedValue) => {
          onChange(selectedValue);
        }}
      >
        <SelectTrigger className={`h-8 text-xs ${widthClassName}`}>
          <SelectValue aria-label={label} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_FILTER_VALUE}>{allLabel}</SelectItem>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

type DocumentationSortField =
  | 'authorizationEndDate'
  | 'censusStart'
  | 'cid'
  | 'cin'
  | 'dob'
  | 'facility'
  | 'firstName'
  | 'healthPlan'
  | 'lastName'
  | 'program'
  | 'qa'
  | 'lcm';

const getDocumentationSortValue = (item: any, field: DocumentationSortField) => {
  if (field === 'authorizationEndDate') {
    return normalizeValue(item.authorizationEndDate);
  }

  if (field === 'censusStart') {
    return normalizeValue(item.censusStart);
  }

  if (field === 'cid') {
    return normalizeValue(item.cid);
  }

  if (field === 'cin') {
    return normalizeValue(item.cin);
  }

  if (field === 'dob') {
    return normalizeValue(item.dob);
  }

  if (field === 'facility') {
    return normalizeValue(item.facility);
  }

  if (field === 'firstName') {
    return normalizeValue(item.firstName);
  }

  if (field === 'healthPlan') {
    return normalizeValue(item.healthPlan);
  }

  if (field === 'program') {
    return normalizeValue(item.program);
  }

  if (field === 'qa') {
    return normalizeValue(item.qa);
  }

  if (field === 'lcm') {
    return normalizeValue(item.lcm);
  }

  return normalizeValue(item.lastName);
};

export const EcmDashboardDocumentationView = ({
  canManageParticipants = false,
}: {
  canManageParticipants?: boolean;
}) => {
  const [searchText, setSearchText] = useState('');
  const [programFilter, setProgramFilter] = useState(ALL_FILTER_VALUE);
  const [facilityFilter, setFacilityFilter] = useState(ALL_FILTER_VALUE);
  const [qaFilter, setQaFilter] = useState(ALL_FILTER_VALUE);
  const [lcmFilter, setLcmFilter] = useState(ALL_FILTER_VALUE);
  const [sortField, setSortField] = useState<DocumentationSortField>('lastName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(1);
  const [notesMonthKey, setNotesMonthKey] = useState(getCurrentMonthKey);
  const [selectedMonthKey, setSelectedMonthKey] = useState(getCurrentMonthKey);
  const [activeActionKey, setActiveActionKey] = useState<string | null>(null);
  const [selectedDetailsItem, setSelectedDetailsItem] = useState<any | null>(null);

  const selectedMonth = parseMonthKey(selectedMonthKey);
  const { data, error, loading, refetch } = useQuery(DOCUMENTATION_QUERY, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: selectedMonth,
  });
  const [updateDetermination] = useMutation(ECM_UPDATE_DETERMINATION_MUTATION);
  const result = data?.ecmDocumentation;
  const monthOptions = buildMonthOptions(result?.dateRangeStart, result?.dateRangeEnd);
  const selectedMonthLabel = getSelectedMonthLabel(selectedMonthKey, monthOptions);
  const notesMonthLabel = getSelectedMonthLabel(notesMonthKey, monthOptions);
  const documentationColumns: EcmFieldOption[] = [
    { id: 'firstName', label: 'First Name' },
    { id: 'lastName', label: 'Last Name' },
    { id: 'program', label: 'Program' },
    { id: 'censusStart', label: 'Date Entered' },
    { id: 'authorizationEndDate', label: 'Auth End Date' },
    { id: 'dob', label: 'DOB' },
    { id: 'cid', label: 'CID' },
    { id: 'cin', label: 'CIN' },
    { id: 'healthPlan', label: 'Health Plan Provider' },
    { id: 'notesEntered', label: 'Notes Entered' },
    { id: 'notesStatus', label: `Notes ${notesMonthLabel} Adequate/Inadequate` },
    { id: 'initialAssessment', label: 'Initial Assessment' },
    { id: 'carePlan', label: 'Care Plan' },
    { id: 'programCompletion', label: 'Program Completion' },
    { id: 'lcm', label: 'LCM' },
    { id: 'qa', label: 'QA' },
  ];
  const { fieldSettingsControl, visibleColumnCount, visibleColumnSet } = useEcmFieldSettings('documentation', documentationColumns);

  useEffect(() => {
    if (monthOptions.length === 0) {
      return;
    }

    const hasSelectedMonth = monthOptions.some((option) => option.value === selectedMonthKey);
    if (!hasSelectedMonth) {
      setSelectedMonthKey(monthOptions[0].value);
      setPage(1);
    }

    const hasSelectedNotesMonth = monthOptions.some((option) => option.value === notesMonthKey);
    if (hasSelectedNotesMonth) {
      return;
    }

    setNotesMonthKey(monthOptions[0].value);
  }, [monthOptions, notesMonthKey, selectedMonthKey]);

  const items = Array.isArray(result?.items) ? result.items : [];
  const programs = uniqueSortedValues(items.map((item: any) => item.program));
  const facilities = uniqueSortedValues(items.map((item: any) => item.facility));
  const qas = uniqueSortedValues(items.map((item: any) => item.qa));
  const lcms = uniqueSortedValues(items.map((item: any) => item.lcm));

  let filteredItems = items.filter((item: any) => {
    if (!matchesFilter(item.program, programFilter)) {
      return false;
    }

    if (!matchesFilter(item.facility, facilityFilter)) {
      return false;
    }

    if (!matchesFilter(item.qa, qaFilter)) {
      return false;
    }

    if (!matchesFilter(item.lcm, lcmFilter)) {
      return false;
    }

    const normalizedSearchText = normalizeLowerCase(searchText);
    if (!normalizedSearchText) {
      return true;
    }

    return buildSearchText([
      item.cid,
      item.cin,
      item.facility,
      item.firstName,
      item.healthPlan,
      item.lastName,
      item.lcm,
      item.program,
      item.qa,
    ]).includes(normalizedSearchText);
  });

  filteredItems = [...filteredItems].sort((left: any, right: any) => {
    const sortValueComparison = compareValues(getDocumentationSortValue(left, sortField), getDocumentationSortValue(right, sortField), sortDirection);
    if (sortValueComparison !== 0) {
      return sortValueComparison;
    }

    const lastNameComparison = compareValues(normalizeValue(left.lastName), normalizeValue(right.lastName), 'asc');
    if (lastNameComparison !== 0) {
      return lastNameComparison;
    }

    return compareValues(normalizeValue(left.firstName), normalizeValue(right.firstName), 'asc');
  });

  const totalCount = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    if (page <= totalPages) {
      return;
    }

    setPage(totalPages);
  }, [page, totalPages]);

  // Pagination is temporarily disabled.
  // const visibleItems = filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const visibleItems = filteredItems;
  const tableColumnCount = visibleColumnCount;
  const documentationExportItems = items.map((item: any) => {
    const notesEntered = countServiceDatesForMonth(item.serviceDates || [], notesMonthKey);
    const notesRequired = getNotesRequired(item.acuity);
    const notesAdequate = notesRequired !== null && notesEntered > 0 && notesEntered >= notesRequired;

    return {
      ...item,
      carePlan: item.carePlanDate,
      initialAssessment: item.initialAssessmentDate,
      notesEntered: `${notesEntered}/${notesRequired ?? '—'}`,
      notesStatus: notesAdequate ? 'Adequate' : 'Needs Attention',
      programCompletion: item.programCompletionDate,
    };
  });

  const handleSort = (field: string) => {
    const normalizedField = field as DocumentationSortField;
    if (sortField === normalizedField) {
      setSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortField(normalizedField);
    setSortDirection('asc');
  };

  const resetFilters = () => {
    setSearchText('');
    setProgramFilter(ALL_FILTER_VALUE);
    setFacilityFilter(ALL_FILTER_VALUE);
    setQaFilter(ALL_FILTER_VALUE);
    setLcmFilter(ALL_FILTER_VALUE);
    setSortField('lastName');
    setSortDirection('asc');
    setPage(1);
  };

  const placeOnHold = async (item: any) => {
    const participantName = [normalizeValue(item.firstName), normalizeValue(item.lastName)].filter(Boolean).join(' ') || 'this participant';
    const actionConfirmed = window.confirm(`Are you sure you want to place ${participantName} on hold?`);
    if (!actionConfirmed) {
      return;
    }

    const actionKey = `on_hold-${item.odrparticipant}`;
    setActiveActionKey(actionKey);
    try {
      const response = await updateDetermination({
        variables: { determination: 'on_hold', odrparticipant: item.odrparticipant },
      });
      if (!response.data?.updateEcmDetermination?.success) {
        throw new Error('Unable to place participant on hold.');
      }
      toast.success('Participant placed on hold.');
      await refetch();
    } catch (error) {
      toast.error(getActionErrorMessage(error, 'Unable to place participant on hold.'));
    } finally {
      setActiveActionKey(null);
    }
  };

  if (loading && !data) {
    return <OdrLoadingSpinner message="Loading ECM Documentation" />;
  }

  if (error && !result) {
    return <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">Unable to load ECM Documentation: {error.message}</div>;
  }

  return (
    <>
    <PanelFrame
      error={error ? `Showing saved results while the latest refresh failed: ${error.message}` : null}
      summary={
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-slate-800">{selectedMonthLabel}</span>
            <span>•</span>
            <span>{totalCount} matching participants</span>
            <span>•</span>
            <span>{items.length} total for month</span>
          </div>
          {/* <PagerControls page={page} totalCount={totalCount} totalPages={totalPages} onPageChange={setPage} /> */}
        </>
      }
      toolbar={
        <div className="flex flex-wrap items-end gap-3">
          <MonthSelectControl monthOptions={monthOptions} selectedMonthKey={selectedMonthKey} selectedMonthLabel={selectedMonthLabel} onChange={setSelectedMonthKey} />
          <MonthSelectControl
            label="Notes Month"
            monthOptions={monthOptions}
            selectedMonthKey={notesMonthKey}
            selectedMonthLabel={notesMonthLabel}
            onChange={setNotesMonthKey}
          />
          <div className="space-y-1">
            <FilterLabel>Search</FilterLabel>
            <Input
              className="h-8 w-[220px] text-xs"
              placeholder="Name, CID, CIN"
              value={searchText}
              onChange={(event) => {
                setSearchText(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <SimpleSelect label="Program" value={programFilter} options={programs} allLabel="All Programs" onChange={setProgramFilter} />
          <SimpleSelect label="Facility" value={facilityFilter} options={facilities} allLabel="All Facilities" onChange={setFacilityFilter} />
          <SimpleSelect label="QA" value={qaFilter} options={qas} allLabel="All QA" onChange={setQaFilter} />
          <SimpleSelect label="LCM" value={lcmFilter} options={lcms} allLabel="All LCM" onChange={setLcmFilter} />
          <EcmResetButton onReset={resetFilters} />
          <div className="ml-auto flex items-center gap-2">
            <EcmExportButton
              onExport={() => {
                exportEcmDataset({
                  columns: documentationColumns,
                  fileName: `ecm-documentation-${selectedMonthKey}-notes-${notesMonthKey}`,
                  rows: documentationExportItems,
                });
              }}
            />
            {fieldSettingsControl}
          </div>
        </div>
      }
    >
      <Table className="min-w-[1900px]">
        <TableHeader className="bg-slate-50">
          <TableRow className="hover:bg-slate-50">
            {visibleColumnSet.has('firstName') ? <TableHead><SortableHeader field="firstName" label="First Name" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('lastName') ? <TableHead><SortableHeader field="lastName" label="Last Name" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('program') ? <TableHead><SortableHeader field="program" label="Program" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('censusStart') ? <TableHead><SortableHeader field="censusStart" label="Date Entered" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('authorizationEndDate') ? <TableHead><SortableHeader field="authorizationEndDate" label="Auth End Date" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('dob') ? <TableHead><SortableHeader field="dob" label="DOB" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('cid') ? <TableHead><SortableHeader field="cid" label="CID" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('cin') ? <TableHead><SortableHeader field="cin" label="CIN" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('healthPlan') ? <TableHead><SortableHeader field="healthPlan" label="Health Plan Provider" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('facility') ? <TableHead><SortableHeader field="facility" label="Facility" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('notesEntered') ? <TableHead>Notes Entered</TableHead> : null}
            {visibleColumnSet.has('notesStatus') ? <TableHead>Notes {notesMonthLabel} Adequate/Inadequate</TableHead> : null}
            {visibleColumnSet.has('initialAssessment') ? <TableHead>Initial Assessment</TableHead> : null}
            {visibleColumnSet.has('carePlan') ? <TableHead>Care Plan</TableHead> : null}
            {visibleColumnSet.has('programCompletion') ? <TableHead>Program Completion</TableHead> : null}
            {visibleColumnSet.has('lcm') ? <TableHead><SortableHeader field="lcm" label="LCM" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('qa') ? <TableHead><SortableHeader field="qa" label="QA" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleItems.length === 0 ? (
            <TableRow className="hover:bg-white">
              <TableCell className="py-10 text-center text-sm text-slate-500" colSpan={tableColumnCount}>
                No participants match the current filters.
              </TableCell>
            </TableRow>
          ) : null}
          {visibleItems.map((item: any) => {
            const notesEntered = countServiceDatesForMonth(item.serviceDates || [], notesMonthKey);
            const notesRequired = getNotesRequired(item.acuity);
            const notesAdequate = notesRequired !== null && notesEntered > 0 && notesEntered >= notesRequired;
            const initialAssessmentStatus = getInitialAssessmentComplianceStatus(item.censusStart, item.initialAssessmentDate);
            const carePlanStatus = getCarePlanComplianceStatus(item.censusStart, item.carePlanDate);
            const programCompletionStatus = getProgramCompletionCompliance(item.programCompletionDate);
            const documentsUrl = `/documents/${item.odrparticipant}`;
            const recordDetailsItem = documentationExportItems.find((candidate: any) => candidate.odrparticipant === item.odrparticipant) || item;

            return (
              <TableRow
                aria-haspopup="dialog"
                aria-label={`View Documentation details for ${getParticipantRecordLabel(item)}`}
                className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--azure-focus-ring)]"
                key={`${item.odrparticipant}-${item.participantId || 'none'}`}
                tabIndex={0}
                onClick={(event) => {
                  if (!isInteractiveRecordTarget(event.target)) {
                    setSelectedDetailsItem(recordDetailsItem);
                  }
                }}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) {
                    return;
                  }
                  event.preventDefault();
                  setSelectedDetailsItem(recordDetailsItem);
                }}
              >
                {visibleColumnSet.has('firstName') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.firstName) || '—'}</TableCell> : null}
                {visibleColumnSet.has('lastName') ? <TableCell className="whitespace-nowrap font-medium text-slate-900">{normalizeValue(item.lastName) || '—'}</TableCell> : null}
                {visibleColumnSet.has('program') ? (
                  <TableCell className="whitespace-nowrap"><ParticipantProfileCellValue item={item} value={item.program} /></TableCell>
                ) : null}
                {visibleColumnSet.has('censusStart') ? <TableCell className="whitespace-nowrap">{formatDate(item.censusStart)}</TableCell> : null}
                {visibleColumnSet.has('authorizationEndDate') ? <TableCell className="whitespace-nowrap">{formatDate(item.authorizationEndDate)}</TableCell> : null}
                {visibleColumnSet.has('dob') ? <TableCell className="whitespace-nowrap">{formatDate(item.dob)}</TableCell> : null}
                {visibleColumnSet.has('cid') ? <TableCell className="whitespace-nowrap"><ParticipantProfileCellValue item={item} value={item.cid} /></TableCell> : null}
                {visibleColumnSet.has('cin') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.cin) || '—'}</TableCell> : null}
                {visibleColumnSet.has('healthPlan') ? <TableCell className="max-w-[12rem]"><TextCell value={item.healthPlan} /></TableCell> : null}
                {visibleColumnSet.has('facility') ? <TableCell className="max-w-[12rem]"><TextCell value={item.facility} /></TableCell> : null}
                {visibleColumnSet.has('notesEntered') ? <TableCell className="whitespace-nowrap">{notesEntered}/{notesRequired ?? '—'}</TableCell> : null}
                {visibleColumnSet.has('notesStatus') ? <TableCell className="whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <StatusIcon status={notesAdequate} />
                    <span>{notesAdequate ? 'Adequate' : 'Needs Attention'}</span>
                  </div>
                </TableCell> : null}
                {visibleColumnSet.has('initialAssessment') ? <TableCell>
                  <DocumentationStatusCell linkLabel="Documents" linkTo={documentsUrl} status={initialAssessmentStatus} value={item.initialAssessmentDate} />
                </TableCell> : null}
                {visibleColumnSet.has('carePlan') ? <TableCell>
                  <DocumentationStatusCell linkLabel="Documents" linkTo={documentsUrl} status={carePlanStatus} value={item.carePlanDate} />
                </TableCell> : null}
                {visibleColumnSet.has('programCompletion') ? <TableCell>
                  <DocumentationStatusCell linkLabel="Documents" linkTo={documentsUrl} status={programCompletionStatus} value={item.programCompletionDate} />
                </TableCell> : null}
                {visibleColumnSet.has('lcm') ? <TableCell className="max-w-[12rem]"><TextCell value={item.lcm} /></TableCell> : null}
                {visibleColumnSet.has('qa') ? <TableCell className="max-w-[12rem]"><TextCell value={item.qa} /></TableCell> : null}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </PanelFrame>
    <EcmRecordDetailsDialog
      actions={selectedDetailsItem ? (
        <EcmRecordActionButton
          disabled={activeActionKey === `on_hold-${selectedDetailsItem.odrparticipant}`}
          icon={<CirclePause className="h-4 w-4" />}
          label="Place On Hold"
          onClick={() => { void placeOnHold(selectedDetailsItem); }}
        />
      ) : null}
      columns={documentationColumns}
      item={selectedDetailsItem}
      onOpenChange={(open) => {
        if (!open) {
          setSelectedDetailsItem(null);
        }
      }}
      title="Documentation Details"
    />
    </>
  );
};

type CalaimSortField =
  | 'cid'
  | 'cin'
  | 'dob'
  | 'ecmEffectiveDate'
  | 'facility'
  | 'firstName'
  | 'healthPlan'
  | 'lastName'
  | 'mrun'
  | 'program'
  | 'qa';

const getCalaimSortValue = (item: any, field: CalaimSortField) => {
  if (field === 'cid') {
    return normalizeValue(item.cid);
  }

  if (field === 'cin') {
    return normalizeValue(item.cin);
  }

  if (field === 'ecmEffectiveDate') {
    return normalizeValue(item.ecmEffectiveDate);
  }

  if (field === 'dob') {
    return normalizeValue(item.dob);
  }

  if (field === 'facility') {
    return normalizeValue(item.facility);
  }

  if (field === 'firstName') {
    return normalizeValue(item.firstName);
  }

  if (field === 'healthPlan') {
    return normalizeValue(item.healthPlan);
  }

  if (field === 'mrun') {
    return normalizeValue(item.mrun);
  }

  if (field === 'program') {
    return normalizeValue(item.program);
  }

  if (field === 'qa') {
    return normalizeValue(item.qa);
  }

  return normalizeValue(item.lastName);
};

export const EcmDashboardCalaimDemographicsView = () => {
  const [searchText, setSearchText] = useState('');
  const [programFilter, setProgramFilter] = useState(ALL_FILTER_VALUE);
  const [facilityFilter, setFacilityFilter] = useState(ALL_FILTER_VALUE);
  const [qaFilter, setQaFilter] = useState(ALL_FILTER_VALUE);
  const [lcmFilter, setLcmFilter] = useState(ALL_FILTER_VALUE);
  const [mrunFilter, setMrunFilter] = useState(ALL_FILTER_VALUE);
  const [sortField, setSortField] = useState<CalaimSortField>('lastName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(1);
  const [selectedMonthKey, setSelectedMonthKey] = useState(getCurrentMonthKey);
  const [selectedDetailsItem, setSelectedDetailsItem] = useState<any | null>(null);
  const { fieldSettingsControl, visibleColumnCount, visibleColumnSet } = useEcmFieldSettings('calaim-demographics', ECM_CALAIM_COLUMNS);

  const selectedMonth = parseMonthKey(selectedMonthKey);
  const { data, error, loading } = useQuery(CALAIM_QUERY, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: selectedMonth,
  });

  const result = data?.ecmCalaimDemographics;
  const monthOptions = buildMonthOptions(result?.dateRangeStart, result?.dateRangeEnd);
  const selectedMonthLabel = getSelectedMonthLabel(selectedMonthKey, monthOptions);

  useEffect(() => {
    if (monthOptions.length === 0) {
      return;
    }

    const hasSelectedMonth = monthOptions.some((option) => option.value === selectedMonthKey);
    if (hasSelectedMonth) {
      return;
    }

    setSelectedMonthKey(monthOptions[0].value);
    setPage(1);
  }, [monthOptions, selectedMonthKey]);

  const items = Array.isArray(result?.items) ? result.items : [];
  const programs = uniqueSortedValues(items.map((item: any) => item.program));
  const facilities = uniqueSortedValues(items.map((item: any) => item.facility));
  const qas = uniqueSortedValues(items.map((item: any) => item.qa));
  const lcms = uniqueSortedValues(items.map((item: any) => item.lcm));
  const mruns = uniqueSortedValues(items.map((item: any) => getMrunStatus(item.mrun)));

  let filteredItems = items.filter((item: any) => {
    if (!matchesFilter(item.program, programFilter)) {
      return false;
    }

    if (!matchesFilter(item.facility, facilityFilter)) {
      return false;
    }

    if (!matchesFilter(item.qa, qaFilter)) {
      return false;
    }

    if (!matchesFilter(item.lcm, lcmFilter)) {
      return false;
    }

    if (mrunFilter !== ALL_FILTER_VALUE && getMrunStatus(item.mrun) !== mrunFilter) {
      return false;
    }

    const normalizedSearchText = normalizeLowerCase(searchText);
    if (!normalizedSearchText) {
      return true;
    }

    return buildSearchText([
      item.cid,
      item.cin,
      item.dob,
      item.ecmProvider,
      item.firstName,
      item.healthPlan,
      item.lastName,
      item.lcm,
      item.mrun,
      item.odrparticipant?.toString(),
      item.pcpName,
      item.program,
      item.qa,
      item.ssn,
    ]).includes(normalizedSearchText);
  });

  filteredItems = [...filteredItems].sort((left: any, right: any) => {
    const sortValueComparison = compareValues(getCalaimSortValue(left, sortField), getCalaimSortValue(right, sortField), sortDirection);
    if (sortValueComparison !== 0) {
      return sortValueComparison;
    }

    const lastNameComparison = compareValues(normalizeValue(left.lastName), normalizeValue(right.lastName), 'asc');
    if (lastNameComparison !== 0) {
      return lastNameComparison;
    }

    return compareValues(normalizeValue(left.firstName), normalizeValue(right.firstName), 'asc');
  });

  const totalCount = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    if (page <= totalPages) {
      return;
    }

    setPage(totalPages);
  }, [page, totalPages]);

  // Pagination is temporarily disabled.
  // const visibleItems = filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const visibleItems = filteredItems;

  const handleSort = (field: string) => {
    const normalizedField = field as CalaimSortField;
    if (sortField === normalizedField) {
      setSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortField(normalizedField);
    setSortDirection('asc');
  };

  const resetFilters = () => {
    setSearchText('');
    setProgramFilter(ALL_FILTER_VALUE);
    setFacilityFilter(ALL_FILTER_VALUE);
    setQaFilter(ALL_FILTER_VALUE);
    setLcmFilter(ALL_FILTER_VALUE);
    setMrunFilter(ALL_FILTER_VALUE);
    setSortField('lastName');
    setSortDirection('asc');
    setPage(1);
  };

  if (loading && !data) {
    return <OdrLoadingSpinner message="Loading ECM CalAIM Demographics" />;
  }

  if (error && !result) {
    return <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">Unable to load ECM CalAIM Demographics: {error.message}</div>;
  }

  return (
    <>
    <PanelFrame
      error={error ? `Showing saved results while the latest refresh failed: ${error.message}` : null}
      summary={
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-slate-800">{selectedMonthLabel}</span>
            <span>•</span>
            <span>{totalCount} matching participants</span>
            <span>•</span>
            <span>{items.length} total for month</span>
          </div>
          {/* <PagerControls page={page} totalCount={totalCount} totalPages={totalPages} onPageChange={setPage} /> */}
        </>
      }
      toolbar={
        <div className="flex flex-wrap items-end gap-3">
          <MonthSelectControl monthOptions={monthOptions} selectedMonthKey={selectedMonthKey} selectedMonthLabel={selectedMonthLabel} onChange={setSelectedMonthKey} />
          <div className="space-y-1">
            <FilterLabel>Search</FilterLabel>
            <Input
              className="h-8 w-[220px] text-xs"
              placeholder="Name, CID, CIN, SSN"
              value={searchText}
              onChange={(event) => {
                setSearchText(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <SimpleSelect label="Program" value={programFilter} options={programs} allLabel="All Programs" onChange={setProgramFilter} />
          <SimpleSelect label="Facility" value={facilityFilter} options={facilities} allLabel="All Facilities" onChange={setFacilityFilter} />
          <SimpleSelect label="QA" value={qaFilter} options={qas} allLabel="All QA" onChange={setQaFilter} />
          <SimpleSelect label="LCM" value={lcmFilter} options={lcms} allLabel="All LCM" onChange={setLcmFilter} />
          <SimpleSelect label="MRUN" value={mrunFilter} options={mruns} allLabel="All MRUN" onChange={setMrunFilter} />
          <EcmResetButton onReset={resetFilters} />
          <div className="ml-auto flex items-center gap-2">
            <EcmExportButton
              onExport={() => {
                exportEcmDataset({
                  columns: ECM_CALAIM_COLUMNS,
                  fileName: `ecm-calaim-demographics-${selectedMonthKey}`,
                  rows: items,
                });
              }}
            />
            {fieldSettingsControl}
          </div>
        </div>
      }
    >
      <Table className="min-w-[2050px]">
        <TableHeader className="bg-slate-50">
          <TableRow className="hover:bg-slate-50">
            {visibleColumnSet.has('firstName') ? <TableHead><SortableHeader field="firstName" label="First Name" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('lastName') ? <TableHead><SortableHeader field="lastName" label="Last Name" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('program') ? <TableHead><SortableHeader field="program" label="Program" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('cid') ? <TableHead><SortableHeader field="cid" label="CID" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('ssn') ? <TableHead>SSN</TableHead> : null}
            {visibleColumnSet.has('cin') ? <TableHead><SortableHeader field="cin" label="MediCal CIN" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('dob') ? <TableHead><SortableHeader field="dob" label="DOB" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('mrun') ? <TableHead><SortableHeader field="mrun" label="MRUN" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('fin') ? <TableHead>FIN</TableHead> : null}
            {visibleColumnSet.has('ecmProvider') ? <TableHead>ECM Provider</TableHead> : null}
            {visibleColumnSet.has('ecmEffectiveDate') ? <TableHead><SortableHeader field="ecmEffectiveDate" label="ECM Effective Date" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('healthPlan') ? <TableHead><SortableHeader field="healthPlan" label="Health Plan Provider" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('dhsEmpaneled') ? <TableHead>Empaneled to DHS</TableHead> : null}
            {visibleColumnSet.has('clinicEmpaneled') ? <TableHead>Empaneled to Clinic</TableHead> : null}
            {visibleColumnSet.has('pcpName') ? <TableHead>PCP Name</TableHead> : null}
            {visibleColumnSet.has('qa') ? <TableHead><SortableHeader field="qa" label="QA" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleItems.length === 0 ? (
            <TableRow className="hover:bg-white">
              <TableCell className="py-10 text-center text-sm text-slate-500" colSpan={visibleColumnCount}>
                No participants match the current filters.
              </TableCell>
            </TableRow>
          ) : null}
          {visibleItems.map((item: any) => (
            <TableRow
              aria-haspopup="dialog"
              aria-label={`View CalAIM Demographics details for ${getParticipantRecordLabel(item)}`}
              className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--azure-focus-ring)]"
              key={`${item.odrparticipant}-${item.participantId || 'none'}`}
              tabIndex={0}
              onClick={(event) => {
                if (!isInteractiveRecordTarget(event.target)) {
                  setSelectedDetailsItem(item);
                }
              }}
              onKeyDown={(event) => {
                if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) {
                  return;
                }
                event.preventDefault();
                setSelectedDetailsItem(item);
              }}
            >
              {visibleColumnSet.has('firstName') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.firstName) || '—'}</TableCell> : null}
              {visibleColumnSet.has('lastName') ? <TableCell className="whitespace-nowrap font-medium text-slate-900">{normalizeValue(item.lastName) || '—'}</TableCell> : null}
              {visibleColumnSet.has('program') ? (
                <TableCell className="whitespace-nowrap"><ParticipantProfileCellValue item={item} value={item.program} /></TableCell>
              ) : null}
              {visibleColumnSet.has('cid') ? <TableCell className="whitespace-nowrap"><ParticipantProfileCellValue item={item} value={item.cid} /></TableCell> : null}
              {visibleColumnSet.has('ssn') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.ssn) || <MissingValue />}</TableCell> : null}
              {visibleColumnSet.has('cin') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.cin) || <MissingValue />}</TableCell> : null}
              {visibleColumnSet.has('dob') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.dob) ? formatDate(item.dob) : <MissingValue />}</TableCell> : null}
              {visibleColumnSet.has('mrun') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.mrun) || <MissingValue />}</TableCell> : null}
              {visibleColumnSet.has('fin') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.fin) || <MissingValue />}</TableCell> : null}
              {visibleColumnSet.has('ecmProvider') ? <TableCell className="max-w-[12rem]">{normalizeValue(item.ecmProvider) || <MissingValue />}</TableCell> : null}
              {visibleColumnSet.has('ecmEffectiveDate') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.ecmEffectiveDate) ? formatDate(item.ecmEffectiveDate) : <MissingValue />}</TableCell> : null}
              {visibleColumnSet.has('healthPlan') ? <TableCell className="max-w-[12rem]">{normalizeValue(item.healthPlan) || <MissingValue />}</TableCell> : null}
              {visibleColumnSet.has('dhsEmpaneled') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.dhsEmpaneled) || <MissingValue />}</TableCell> : null}
              {visibleColumnSet.has('clinicEmpaneled') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.clinicEmpaneled) || <MissingValue />}</TableCell> : null}
              {visibleColumnSet.has('pcpName') ? <TableCell className="max-w-[12rem]">{normalizeValue(item.pcpName) || <MissingValue />}</TableCell> : null}
              {visibleColumnSet.has('qa') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.qa) ? normalizeValue(item.qa).split(' ')[0] : <MissingValue />}</TableCell> : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </PanelFrame>
    <EcmRecordDetailsDialog
      columns={ECM_CALAIM_COLUMNS}
      item={selectedDetailsItem}
      onOpenChange={(open) => {
        if (!open) {
          setSelectedDetailsItem(null);
        }
      }}
      title="CalAIM Demographics Details"
    />
    </>
  );
};

export const EcmDashboardOutreachView = ({
  canManageParticipants = false,
}: {
  canManageParticipants?: boolean;
}) => {
  const [searchText, setSearchText] = useState('');
  const [programFilter, setProgramFilter] = useState(ALL_FILTER_VALUE);
  const [statusFilter, setStatusFilter] = useState<'active' | 'complete'>('active');
  const [sortField, setSortField] = useState<'cid' | 'cin' | 'dob' | 'firstName' | 'healthPlan' | 'lastName' | 'outreachEnd' | 'outreachStart' | 'program' | 'qa'>('outreachStart');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [page, setPage] = useState(1);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [activeActionKey, setActiveActionKey] = useState<string | null>(null);
  const [selectedDetailsItem, setSelectedDetailsItem] = useState<any | null>(null);
  const { fieldSettingsControl, visibleColumnCount, visibleColumnSet } = useEcmFieldSettings('outreach', ECM_OUTREACH_COLUMNS);

  const { data, error, loading, refetch } = useQuery(OUTREACH_QUERY, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const [addOutreachParticipant] = useMutation(ECM_ADD_OUTREACH_PARTICIPANT_MUTATION);
  const [moveOutreachParticipant] = useMutation(ECM_MOVE_OUTREACH_PARTICIPANT_MUTATION);
  const [removeOutreachParticipant] = useMutation(ECM_REMOVE_OUTREACH_PARTICIPANT_MUTATION);

  const items = Array.isArray(data?.ecmOutreach?.items) ? data.ecmOutreach.items : [];
  const programs = uniqueSortedValues(items.flatMap((item: any) => item.programs || [item.program]));
  const preparedItems = items.map((item: any) => ({
    ...item,
    attemptData: calculateOutreachAttempts(item.outreachAttempts, item.outreachStart),
  }));
  const outreachExportItems = preparedItems.map((item: any) => ({
    ...item,
    attempt1: formatOutreachAttemptForExport(item.attemptData.attempts[0]),
    attempt2: formatOutreachAttemptForExport(item.attemptData.attempts[1]),
    attempt3: formatOutreachAttemptForExport(item.attemptData.attempts[2]),
    nextStep: item.attemptData.status === 'enrolled'
      ? 'Move to Census'
      : item.attemptData.status === 'rejected'
        ? 'Move to Opt Out'
        : `${formatDate(item.attemptData.nextAttemptDate)} follow-up`,
  }));

  let filteredItems = preparedItems.filter((item: any) => {
    const isComplete = item.attemptData.status === 'enrolled' || item.attemptData.status === 'rejected';
    if (statusFilter === 'complete' && !isComplete) {
      return false;
    }

    if (statusFilter === 'active' && isComplete) {
      return false;
    }

    if (programFilter !== ALL_FILTER_VALUE && !(item.programs || []).includes(programFilter) && item.program !== programFilter) {
      return false;
    }

    const normalizedSearchText = normalizeLowerCase(searchText);
    if (!normalizedSearchText) {
      return true;
    }

    return buildSearchText([
      item.cid,
      item.cin,
      item.dob,
      item.firstName,
      item.healthPlan,
      item.lastName,
      item.notes,
      item.program,
      item.qa,
    ]).includes(normalizedSearchText);
  });

  filteredItems = [...filteredItems].sort((left: any, right: any) => {
    const sortValueComparison = compareValues(normalizeValue(left[sortField]), normalizeValue(right[sortField]), sortDirection);
    if (sortValueComparison !== 0) {
      return sortValueComparison;
    }

    const lastNameComparison = compareValues(normalizeValue(left.lastName), normalizeValue(right.lastName), 'asc');
    if (lastNameComparison !== 0) {
      return lastNameComparison;
    }

    return compareValues(normalizeValue(left.firstName), normalizeValue(right.firstName), 'asc');
  });

  const totalCount = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    if (page <= totalPages) {
      return;
    }

    setPage(totalPages);
  }, [page, totalPages]);

  // Pagination is temporarily disabled.
  // const visibleItems = filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const visibleItems = filteredItems;
  const tableColumnCount = visibleColumnCount;

  const runAction = async ({
    action,
    actionKey,
    errorMessage,
    rethrowOnError = false,
    successMessage,
  }: {
    action: () => Promise<void>;
    actionKey: string;
    errorMessage: string;
    rethrowOnError?: boolean;
    successMessage: string;
  }) => {
    setActiveActionKey(actionKey);
    try {
      await action();
      toast.success(successMessage);
      await refetch();
    } catch (error) {
      toast.error(getActionErrorMessage(error, errorMessage));
      if (rethrowOnError) {
        throw error;
      }
    } finally {
      setActiveActionKey(null);
    }
  };

  const addParticipantToOutreach = async ({ odrparticipant }: { odrparticipant: number }) => {
    await runAction({
      action: async () => {
        const response = await addOutreachParticipant({ variables: { odrparticipant } });
        if (!response.data?.addEcmOutreachParticipant?.success) {
          throw new Error('Unable to add participant to Outreach.');
        }
      },
      actionKey: `add-${odrparticipant}`,
      errorMessage: 'Unable to add participant to Outreach.',
      rethrowOnError: true,
      successMessage: 'Participant added to Outreach.',
    });
  };

  const moveParticipantToCensus = async (item: any) => {
    const participantName = [normalizeValue(item.firstName), normalizeValue(item.lastName)].filter(Boolean).join(' ') || 'this participant';
    const moveConfirmed = window.confirm(`Move ${participantName} from Outreach to Census?`);
    if (!moveConfirmed) {
      return;
    }

    await runAction({
      action: async () => {
        const response = await moveOutreachParticipant({
          variables: { destination: 'census', odrparticipant: item.odrparticipant },
        });
        if (!response.data?.moveEcmOutreachParticipant?.success) {
          throw new Error('Unable to move participant to Census.');
        }
      },
      actionKey: `move-to-census-${item.odrparticipant}`,
      errorMessage: 'Unable to move participant to Census.',
      successMessage: 'Participant moved to Census.',
    });
  };

  const moveParticipantToOptOut = async (item: any) => {
    const participantName = [normalizeValue(item.firstName), normalizeValue(item.lastName)].filter(Boolean).join(' ') || 'this participant';
    const moveConfirmed = window.confirm(`Move ${participantName} from Outreach to Opt Out?`);
    if (!moveConfirmed) {
      return;
    }

    await runAction({
      action: async () => {
        const response = await moveOutreachParticipant({
          variables: { destination: 'opt_out', odrparticipant: item.odrparticipant },
        });
        if (!response.data?.moveEcmOutreachParticipant?.success) {
          throw new Error('Unable to move participant to Opt Out.');
        }
      },
      actionKey: `move-to-opt-out-${item.odrparticipant}`,
      errorMessage: 'Unable to move participant to Opt Out.',
      successMessage: 'Participant moved to Opt Out.',
    });
  };

  const removeParticipantFromOutreach = async (item: any) => {
    const participantName = [normalizeValue(item.firstName), normalizeValue(item.lastName)].filter(Boolean).join(' ') || 'this participant';
    const participantCin = normalizeValue(item.cin);
    const removalConfirmed = window.confirm(
      `Remove ${participantName}${participantCin ? ` (CIN ${participantCin})` : ''} from Outreach?`
    );
    if (!removalConfirmed) {
      return;
    }

    await runAction({
      action: async () => {
        const response = await removeOutreachParticipant({ variables: { odrparticipant: item.odrparticipant } });
        if (!response.data?.removeEcmOutreachParticipant?.success) {
          throw new Error('Unable to remove participant from Outreach.');
        }
      },
      actionKey: `remove-${item.odrparticipant}`,
      errorMessage: 'Unable to remove participant from Outreach.',
      successMessage: 'Participant removed from Outreach.',
    });
  };

  const handleSort = (field: string) => {
    const normalizedField = field as typeof sortField;
    if (sortField === normalizedField) {
      setSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortField(normalizedField);
    setSortDirection('asc');
  };

  const resetFilters = () => {
    setSearchText('');
    setProgramFilter(ALL_FILTER_VALUE);
    setSortField('outreachStart');
    setSortDirection('desc');
    setPage(1);
  };

  if (loading && !data) {
    return <OdrLoadingSpinner message="Loading ECM Outreach" />;
  }

  if (error && !data?.ecmOutreach) {
    return <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">Unable to load ECM Outreach: {error.message}</div>;
  }

  return (
    <>
      <PanelFrame
        error={error ? `Showing saved results while the latest refresh failed: ${error.message}` : null}
        summary={
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-slate-800">{statusFilter === 'active' ? 'Active Outreach' : 'Completed Outreach'}</span>
              <span>•</span>
              <span>{totalCount} matching participants</span>
              <span>•</span>
              <span>{preparedItems.length} total</span>
            </div>
            {/* <PagerControls page={page} totalCount={totalCount} totalPages={totalPages} onPageChange={setPage} /> */}
          </>
        }
        toolbar={
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <FilterLabel>View</FilterLabel>
              <div className="flex items-center gap-2">
                <Button className="h-8" size="sm" variant={statusFilter === 'active' ? 'default' : 'outline'} onClick={() => setStatusFilter('active')}>
                  Active
                </Button>
                <Button className="h-8" size="sm" variant={statusFilter === 'complete' ? 'default' : 'outline'} onClick={() => setStatusFilter('complete')}>
                  Complete
                </Button>
              </div>
            </div>
            <div className="space-y-1">
              <FilterLabel>Search</FilterLabel>
              <Input
                className="h-8 w-[240px] text-xs"
                placeholder="Name, CID, CIN"
                value={searchText}
                onChange={(event) => {
                  setSearchText(event.target.value);
                  setPage(1);
                }}
              />
            </div>
            <SimpleSelect label="Program" value={programFilter} options={programs} allLabel="All Programs" onChange={setProgramFilter} />
            <EcmResetButton onReset={resetFilters} />
            <div className="ml-auto flex items-center gap-2">
              {canManageParticipants ? (
                <Button className="h-8" size="sm" onClick={() => setIsAddDialogOpen(true)}>
                  Add to Outreach
                </Button>
              ) : null}
              <EcmExportButton
                onExport={() => {
                  exportEcmDataset({
                    columns: ECM_OUTREACH_COLUMNS,
                    fileName: 'ecm-outreach',
                    rows: outreachExportItems,
                  });
                }}
              />
              {fieldSettingsControl}
            </div>
          </div>
        }
      >
        <Table className="min-w-[1800px]">
          <TableHeader className="bg-slate-50">
            <TableRow className="hover:bg-slate-50">
              {visibleColumnSet.has('firstName') ? <TableHead><SortableHeader field="firstName" label="First Name" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
              {visibleColumnSet.has('lastName') ? <TableHead><SortableHeader field="lastName" label="Last Name" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
              {visibleColumnSet.has('program') ? <TableHead><SortableHeader field="program" label="Program" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
              {visibleColumnSet.has('outreachStart') ? <TableHead><SortableHeader field="outreachStart" label="Outreach Start" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
              {visibleColumnSet.has('outreachEnd') ? <TableHead><SortableHeader field="outreachEnd" label="Outreach End" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
              {visibleColumnSet.has('cid') ? <TableHead><SortableHeader field="cid" label="CID" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
              {visibleColumnSet.has('cin') ? <TableHead><SortableHeader field="cin" label="CIN" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
              {visibleColumnSet.has('dob') ? <TableHead><SortableHeader field="dob" label="DOB" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
              {visibleColumnSet.has('healthPlan') ? <TableHead><SortableHeader field="healthPlan" label="Health Plan Provider" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
              {visibleColumnSet.has('notes') ? <TableHead>Notes</TableHead> : null}
              {visibleColumnSet.has('attempt1') ? <TableHead>Attempt 1</TableHead> : null}
              {visibleColumnSet.has('attempt2') ? <TableHead>Attempt 2</TableHead> : null}
              {visibleColumnSet.has('attempt3') ? <TableHead>Attempt 3</TableHead> : null}
              {visibleColumnSet.has('nextStep') ? <TableHead>Next Step</TableHead> : null}
              {visibleColumnSet.has('qa') ? <TableHead><SortableHeader field="qa" label="QA" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleItems.length === 0 ? (
              <TableRow className="hover:bg-white">
                <TableCell className="py-10 text-center text-sm text-slate-500" colSpan={tableColumnCount}>
                  No participants match the current filters.
                </TableCell>
              </TableRow>
            ) : null}
            {visibleItems.map((item: any) => {
              const rowActionInProgress = [
                `move-to-census-${item.odrparticipant}`,
                `move-to-opt-out-${item.odrparticipant}`,
                `remove-${item.odrparticipant}`,
              ].includes(activeActionKey || '');
              const recordDetailsItem = outreachExportItems.find((candidate: any) => candidate.odrparticipant === item.odrparticipant) || item;

              return (
                <TableRow
                  aria-haspopup="dialog"
                  aria-label={`View Outreach details for ${getParticipantRecordLabel(item)}`}
                  className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--azure-focus-ring)]"
                  key={`${item.odrparticipant}-${item.participantId || 'none'}`}
                  tabIndex={0}
                  onClick={(event) => {
                    if (!isInteractiveRecordTarget(event.target)) {
                      setSelectedDetailsItem(recordDetailsItem);
                    }
                  }}
                  onKeyDown={(event) => {
                    if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) {
                      return;
                    }
                    event.preventDefault();
                    setSelectedDetailsItem(recordDetailsItem);
                  }}
                >
                  {visibleColumnSet.has('firstName') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.firstName) || '—'}</TableCell> : null}
                  {visibleColumnSet.has('lastName') ? <TableCell className="whitespace-nowrap font-medium text-slate-900">{normalizeValue(item.lastName) || '—'}</TableCell> : null}
                  {visibleColumnSet.has('program') ? (
                    <TableCell className="whitespace-nowrap"><ParticipantProfileCellValue item={item} value={item.program} /></TableCell>
                  ) : null}
                  {visibleColumnSet.has('outreachStart') ? <TableCell className="whitespace-nowrap">{formatDate(item.outreachStart)}</TableCell> : null}
                  {visibleColumnSet.has('outreachEnd') ? <TableCell className="whitespace-nowrap">{formatDate(item.outreachEnd)}</TableCell> : null}
                  {visibleColumnSet.has('cid') ? <TableCell className="whitespace-nowrap"><ParticipantProfileCellValue item={item} value={item.cid} /></TableCell> : null}
                  {visibleColumnSet.has('cin') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.cin) || '—'}</TableCell> : null}
                  {visibleColumnSet.has('dob') ? <TableCell className="whitespace-nowrap">{formatDate(item.dob)}</TableCell> : null}
                  {visibleColumnSet.has('healthPlan') ? <TableCell className="max-w-[12rem]"><TextCell value={item.healthPlan} /></TableCell> : null}
                  {visibleColumnSet.has('notes') ? <TableCell className="max-w-[14rem]"><TextCell value={item.notes} /></TableCell> : null}
                  {visibleColumnSet.has('attempt1') ? <TableCell><AttemptCell attempt={item.attemptData.attempts[0]} /></TableCell> : null}
                  {visibleColumnSet.has('attempt2') ? <TableCell><AttemptCell attempt={item.attemptData.attempts[1]} /></TableCell> : null}
                  {visibleColumnSet.has('attempt3') ? <TableCell><AttemptCell attempt={item.attemptData.attempts[2]} /></TableCell> : null}
                  {visibleColumnSet.has('nextStep') ? (
                    <TableCell className="whitespace-nowrap">
                      {item.attemptData.status === 'enrolled'
                        ? canManageParticipants
                          ? (
                            <Button
                              className="h-8"
                              disabled={rowActionInProgress}
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                void moveParticipantToCensus(item);
                              }}
                            >
                              Move to Census
                            </Button>
                          )
                          : 'Move to Census'
                        : null}
                      {item.attemptData.status === 'rejected'
                        ? canManageParticipants
                          ? (
                            <Button
                              className="h-8"
                              disabled={rowActionInProgress}
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                void moveParticipantToOptOut(item);
                              }}
                            >
                              Move to Opt Out
                            </Button>
                          )
                          : 'Move to Opt Out'
                        : null}
                      {item.attemptData.status !== 'enrolled' && item.attemptData.status !== 'rejected'
                        ? `${formatDate(item.attemptData.nextAttemptDate)} follow-up`
                        : null}
                    </TableCell>
                  ) : null}
                  {visibleColumnSet.has('qa') ? <TableCell className="max-w-[10rem]"><TextCell value={item.qa} /></TableCell> : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </PanelFrame>
      <EcmRecordDetailsDialog
        actions={selectedDetailsItem ? (
          <EcmRecordActionButton
            disabled={activeActionKey === `remove-${selectedDetailsItem.odrparticipant}`}
            icon={<Trash2 className="h-4 w-4" />}
            label="Remove from Outreach"
            variant="destructive"
            onClick={() => { void removeParticipantFromOutreach(selectedDetailsItem); }}
          />
        ) : null}
        columns={ECM_OUTREACH_COLUMNS}
        item={selectedDetailsItem}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedDetailsItem(null);
          }
        }}
        title="Outreach Details"
      />
      <EcmAddParticipantDialog
        closeLabel="Close"
        description="Search by participant name, CID, or CIN, then add the participant to active outreach."
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onSubmit={addParticipantToOutreach}
        submitLabel="Add to Outreach"
        title="Add Participant to Outreach"
      />
    </>
  );
};

export const EcmDashboardDischargeView = () => {
  const [mode, setMode] = useState<'discharges' | 'opt-outs'>('discharges');
  const [searchText, setSearchText] = useState('');
  const [programFilter, setProgramFilter] = useState(ALL_FILTER_VALUE);
  const [facilityFilter, setFacilityFilter] = useState(ALL_FILTER_VALUE);
  const [qaFilter, setQaFilter] = useState(ALL_FILTER_VALUE);
  const [lcmFilter, setLcmFilter] = useState(ALL_FILTER_VALUE);
  const [sortField, setSortField] = useState<'cid' | 'cin' | 'dob' | 'endDate' | 'enteredDate' | 'facility' | 'firstName' | 'healthPlan' | 'lastName' | 'lcm' | 'program' | 'qa'>('endDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [page, setPage] = useState(1);
  const [selectedDetailsItem, setSelectedDetailsItem] = useState<any | null>(null);
  const { fieldSettingsControl, visibleColumnCount, visibleColumnSet } = useEcmFieldSettings('discharge', ECM_DISCHARGE_COLUMNS);

  const { data, error, loading } = useQuery(DISCHARGE_QUERY, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: { mode },
  });

  const items = Array.isArray(data?.ecmDischarge?.items) ? data.ecmDischarge.items : [];
  const programs = uniqueSortedValues(items.flatMap((item: any) => item.programs || [item.program]));
  const facilities = uniqueSortedValues(items.map((item: any) => item.facility));
  const qas = uniqueSortedValues(items.map((item: any) => item.qa));
  const lcms = uniqueSortedValues(items.map((item: any) => item.lcm));

  let filteredItems = items.filter((item: any) => {
    if (programFilter !== ALL_FILTER_VALUE && !(item.programs || []).includes(programFilter) && item.program !== programFilter) {
      return false;
    }

    if (!matchesFilter(item.facility, facilityFilter)) {
      return false;
    }

    if (!matchesFilter(item.qa, qaFilter)) {
      return false;
    }

    if (!matchesFilter(item.lcm, lcmFilter)) {
      return false;
    }

    const normalizedSearchText = normalizeLowerCase(searchText);
    if (!normalizedSearchText) {
      return true;
    }

    return buildSearchText([
      item.address,
      item.cid,
      item.cin,
      item.facility,
      item.firstName,
      item.healthPlan,
      item.lastName,
      item.lcm,
      item.notes,
      item.phoneNumber,
      item.program,
      item.qa,
    ]).includes(normalizedSearchText);
  });

  filteredItems = [...filteredItems].sort((left: any, right: any) => {
    const sortValueComparison = compareValues(normalizeValue(left[sortField]), normalizeValue(right[sortField]), sortDirection);
    if (sortValueComparison !== 0) {
      return sortValueComparison;
    }

    const lastNameComparison = compareValues(normalizeValue(left.lastName), normalizeValue(right.lastName), 'asc');
    if (lastNameComparison !== 0) {
      return lastNameComparison;
    }

    return compareValues(normalizeValue(left.firstName), normalizeValue(right.firstName), 'asc');
  });

  const totalCount = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    if (page <= totalPages) {
      return;
    }

    setPage(totalPages);
  }, [page, totalPages]);

  // Pagination is temporarily disabled.
  // const visibleItems = filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const visibleItems = filteredItems;
  const dischargeExportItems = items.map((item: any) => ({
    ...item,
    totalDays: getDaysDifference(item.enteredDate, item.endDate),
  }));

  const handleSort = (field: string) => {
    const normalizedField = field as typeof sortField;
    if (sortField === normalizedField) {
      setSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortField(normalizedField);
    setSortDirection('asc');
  };

  const resetFilters = () => {
    setSearchText('');
    setProgramFilter(ALL_FILTER_VALUE);
    setFacilityFilter(ALL_FILTER_VALUE);
    setQaFilter(ALL_FILTER_VALUE);
    setLcmFilter(ALL_FILTER_VALUE);
    setSortField('endDate');
    setSortDirection('desc');
    setPage(1);
  };

  if (loading && !data) {
    return <OdrLoadingSpinner message="Loading ECM Discharge" />;
  }

  if (error && !data?.ecmDischarge) {
    return <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">Unable to load ECM Discharge: {error.message}</div>;
  }

  return (
    <>
    <PanelFrame
      error={error ? `Showing saved results while the latest refresh failed: ${error.message}` : null}
      summary={
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-slate-800">{mode === 'discharges' ? 'Discharges' : 'Opt Outs'}</span>
            <span>•</span>
            <span>{totalCount} matching participants</span>
            <span>•</span>
            <span>{items.length} total</span>
          </div>
          {/* <PagerControls page={page} totalCount={totalCount} totalPages={totalPages} onPageChange={setPage} /> */}
        </>
      }
      toolbar={
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <FilterLabel>View</FilterLabel>
            <div className="flex items-center gap-2">
              <Button className="h-8" size="sm" variant={mode === 'discharges' ? 'default' : 'outline'} onClick={() => setMode('discharges')}>
                Discharges
              </Button>
              <Button className="h-8" size="sm" variant={mode === 'opt-outs' ? 'default' : 'outline'} onClick={() => setMode('opt-outs')}>
                Opt Outs
              </Button>
            </div>
          </div>
          <div className="space-y-1">
            <FilterLabel>Search</FilterLabel>
            <Input
              className="h-8 w-[240px] text-xs"
              placeholder="Name, CID, CIN"
              value={searchText}
              onChange={(event) => {
                setSearchText(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <SimpleSelect label="Program" value={programFilter} options={programs} allLabel="All Programs" onChange={setProgramFilter} />
          <SimpleSelect label="Facility" value={facilityFilter} options={facilities} allLabel="All Facilities" onChange={setFacilityFilter} />
          <SimpleSelect label="QA" value={qaFilter} options={qas} allLabel="All QA" onChange={setQaFilter} />
          <SimpleSelect label="LCM" value={lcmFilter} options={lcms} allLabel="All LCM" onChange={setLcmFilter} />
          <EcmResetButton onReset={resetFilters} />
          <div className="ml-auto flex items-center gap-2">
            <EcmExportButton
              onExport={() => {
                exportEcmDataset({
                  columns: ECM_DISCHARGE_COLUMNS,
                  fileName: `ecm-${mode}`,
                  rows: dischargeExportItems,
                });
              }}
            />
            {fieldSettingsControl}
          </div>
        </div>
      }
    >
      <Table className="min-w-[2000px]">
        <TableHeader className="bg-slate-50">
          <TableRow className="hover:bg-slate-50">
            {visibleColumnSet.has('firstName') ? <TableHead><SortableHeader field="firstName" label="First Name" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('lastName') ? <TableHead><SortableHeader field="lastName" label="Last Name" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('program') ? <TableHead><SortableHeader field="program" label="Program" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('enteredDate') ? <TableHead><SortableHeader field="enteredDate" label="Date Entered" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('endDate') ? <TableHead><SortableHeader field="endDate" label={mode === 'discharges' ? 'Date Discharged' : 'Date Opted Out'} onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('totalDays') ? <TableHead>Total Days</TableHead> : null}
            {visibleColumnSet.has('address') ? <TableHead>Address</TableHead> : null}
            {visibleColumnSet.has('phoneNumber') ? <TableHead>Phone #</TableHead> : null}
            {visibleColumnSet.has('notes') ? <TableHead>Notes</TableHead> : null}
            {visibleColumnSet.has('cid') ? <TableHead><SortableHeader field="cid" label="CID" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('cin') ? <TableHead><SortableHeader field="cin" label="CIN" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('healthPlan') ? <TableHead><SortableHeader field="healthPlan" label="Health Plan Provider" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('facility') ? <TableHead><SortableHeader field="facility" label="Facility" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('lcm') ? <TableHead><SortableHeader field="lcm" label="LCM" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('dob') ? <TableHead><SortableHeader field="dob" label="DOB" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('qa') ? <TableHead><SortableHeader field="qa" label="QA" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleItems.length === 0 ? (
            <TableRow className="hover:bg-white">
              <TableCell className="py-10 text-center text-sm text-slate-500" colSpan={visibleColumnCount}>
                No participants match the current filters.
              </TableCell>
            </TableRow>
          ) : null}
          {visibleItems.map((item: any) => {
            const recordDetailsItem = dischargeExportItems.find((candidate: any) => candidate.odrparticipant === item.odrparticipant) || item;
            return (
            <TableRow
              aria-haspopup="dialog"
              aria-label={`View Discharge details for ${getParticipantRecordLabel(item)}`}
              className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--azure-focus-ring)]"
              key={`${item.odrparticipant}-${item.participantId || 'none'}`}
              tabIndex={0}
              onClick={(event) => {
                if (!isInteractiveRecordTarget(event.target)) {
                  setSelectedDetailsItem(recordDetailsItem);
                }
              }}
              onKeyDown={(event) => {
                if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) {
                  return;
                }
                event.preventDefault();
                setSelectedDetailsItem(recordDetailsItem);
              }}
            >
              {visibleColumnSet.has('firstName') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.firstName) || '—'}</TableCell> : null}
              {visibleColumnSet.has('lastName') ? <TableCell className="whitespace-nowrap font-medium text-slate-900">{normalizeValue(item.lastName) || '—'}</TableCell> : null}
              {visibleColumnSet.has('program') ? (
                <TableCell className="whitespace-nowrap"><ParticipantProfileCellValue item={item} value={item.program} /></TableCell>
              ) : null}
              {visibleColumnSet.has('enteredDate') ? <TableCell className="whitespace-nowrap">{formatDate(item.enteredDate)}</TableCell> : null}
              {visibleColumnSet.has('endDate') ? <TableCell className="whitespace-nowrap">{formatDate(item.endDate)}</TableCell> : null}
              {visibleColumnSet.has('totalDays') ? <TableCell className="whitespace-nowrap">{getDaysDifference(item.enteredDate, item.endDate) ?? '—'}</TableCell> : null}
              {visibleColumnSet.has('address') ? <TableCell className="max-w-[14rem]"><TextCell value={item.address} /></TableCell> : null}
              {visibleColumnSet.has('phoneNumber') ? <TableCell className="whitespace-nowrap"><TextCell value={item.phoneNumber} /></TableCell> : null}
              {visibleColumnSet.has('notes') ? <TableCell className="max-w-[14rem]"><TextCell value={item.notes} /></TableCell> : null}
              {visibleColumnSet.has('cid') ? <TableCell className="whitespace-nowrap"><ParticipantProfileCellValue item={item} value={item.cid} /></TableCell> : null}
              {visibleColumnSet.has('cin') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.cin) || '—'}</TableCell> : null}
              {visibleColumnSet.has('healthPlan') ? <TableCell className="max-w-[12rem]"><TextCell value={item.healthPlan} /></TableCell> : null}
              {visibleColumnSet.has('facility') ? <TableCell className="max-w-[12rem]"><TextCell value={item.facility} /></TableCell> : null}
              {visibleColumnSet.has('lcm') ? <TableCell className="max-w-[12rem]"><TextCell value={item.lcm} /></TableCell> : null}
              {visibleColumnSet.has('dob') ? <TableCell className="whitespace-nowrap">{formatDate(item.dob)}</TableCell> : null}
              {visibleColumnSet.has('qa') ? <TableCell className="max-w-[10rem]"><TextCell value={item.qa} /></TableCell> : null}
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </PanelFrame>
    <EcmRecordDetailsDialog
      columns={ECM_DISCHARGE_COLUMNS}
      item={selectedDetailsItem}
      onOpenChange={(open) => {
        if (!open) {
          setSelectedDetailsItem(null);
        }
      }}
      title="Discharge Details"
    />
    </>
  );
};

export const EcmDashboardProviderMedicalDemographicsView = () => {
  const [searchText, setSearchText] = useState('');
  const [facilityFilter, setFacilityFilter] = useState(ALL_FILTER_VALUE);
  const [qaFilter, setQaFilter] = useState(ALL_FILTER_VALUE);
  const [lcmFilter, setLcmFilter] = useState(ALL_FILTER_VALUE);
  const [mrunFilter, setMrunFilter] = useState(ALL_FILTER_VALUE);
  const [sortField, setSortField] = useState<'cid' | 'cin' | 'dob' | 'facility' | 'firstName' | 'healthPlan' | 'lastName' | 'lcm' | 'mrun' | 'qa'>('lastName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(1);
  const [selectedDetailsItem, setSelectedDetailsItem] = useState<any | null>(null);
  const { fieldSettingsControl, visibleColumnCount, visibleColumnSet } = useEcmFieldSettings('provider-medical-demographics', ECM_PROVIDER_MEDICAL_COLUMNS);

  const { data, error, loading } = useQuery(PROVIDER_MEDICAL_QUERY, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const items = Array.isArray(data?.ecmProviderMedicalDemographics?.items) ? data.ecmProviderMedicalDemographics.items : [];
  const facilities = uniqueSortedValues(items.map((item: any) => item.facility));
  const qas = uniqueSortedValues(items.map((item: any) => item.qa));
  const lcms = uniqueSortedValues(items.map((item: any) => item.lcm));
  const mruns = uniqueSortedValues(items.map((item: any) => getMrunStatus(item.mrun)));

  let filteredItems = items.filter((item: any) => {
    if (!matchesFilter(item.facility, facilityFilter)) {
      return false;
    }

    if (!matchesFilter(item.qa, qaFilter)) {
      return false;
    }

    if (!matchesFilter(item.lcm, lcmFilter)) {
      return false;
    }

    if (mrunFilter !== ALL_FILTER_VALUE && getMrunStatus(item.mrun) !== mrunFilter) {
      return false;
    }

    const normalizedSearchText = normalizeLowerCase(searchText);
    if (!normalizedSearchText) {
      return true;
    }

    return buildSearchText([
      item.cid,
      item.cin,
      item.dob,
      item.firstName,
      item.healthPlan,
      item.lastName,
      item.lcm,
      item.mrun,
      item.notes,
      item.odrparticipant?.toString(),
      item.qa,
      item.ssn,
    ]).includes(normalizedSearchText);
  });

  filteredItems = [...filteredItems].sort((left: any, right: any) => {
    const sortValueComparison = compareValues(normalizeValue(left[sortField]), normalizeValue(right[sortField]), sortDirection);
    if (sortValueComparison !== 0) {
      return sortValueComparison;
    }

    const lastNameComparison = compareValues(normalizeValue(left.lastName), normalizeValue(right.lastName), 'asc');
    if (lastNameComparison !== 0) {
      return lastNameComparison;
    }

    return compareValues(normalizeValue(left.firstName), normalizeValue(right.firstName), 'asc');
  });

  const totalCount = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    if (page <= totalPages) {
      return;
    }

    setPage(totalPages);
  }, [page, totalPages]);

  // Pagination is temporarily disabled.
  // const visibleItems = filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const visibleItems = filteredItems;

  const handleSort = (field: string) => {
    const normalizedField = field as typeof sortField;
    if (sortField === normalizedField) {
      setSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortField(normalizedField);
    setSortDirection('asc');
  };

  const resetFilters = () => {
    setSearchText('');
    setFacilityFilter(ALL_FILTER_VALUE);
    setQaFilter(ALL_FILTER_VALUE);
    setLcmFilter(ALL_FILTER_VALUE);
    setMrunFilter(ALL_FILTER_VALUE);
    setSortField('lastName');
    setSortDirection('asc');
    setPage(1);
  };

  if (loading && !data) {
    return <OdrLoadingSpinner message="Loading Provider Medi-Cal Demographics" />;
  }

  if (error && !data?.ecmProviderMedicalDemographics) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
        Unable to load Provider Medi-Cal Demographics: {error.message}
      </div>
    );
  }

  return (
    <>
    <PanelFrame
      error={error ? `Showing saved results while the latest refresh failed: ${error.message}` : null}
      summary={
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-slate-800">{totalCount} matching participants</span>
            <span>•</span>
            <span>{items.length} total</span>
          </div>
          {/* <PagerControls page={page} totalCount={totalCount} totalPages={totalPages} onPageChange={setPage} /> */}
        </>
      }
      toolbar={
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <FilterLabel>Search</FilterLabel>
            <Input
              className="h-8 w-[240px] text-xs"
              placeholder="Name, CID, CIN, SSN"
              value={searchText}
              onChange={(event) => {
                setSearchText(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <SimpleSelect label="Facility" value={facilityFilter} options={facilities} allLabel="All Facilities" onChange={setFacilityFilter} />
          <SimpleSelect label="QA" value={qaFilter} options={qas} allLabel="All QA" onChange={setQaFilter} />
          <SimpleSelect label="LCM" value={lcmFilter} options={lcms} allLabel="All LCM" onChange={setLcmFilter} />
          <SimpleSelect label="MRUN" value={mrunFilter} options={mruns} allLabel="All MRUN" onChange={setMrunFilter} />
          <EcmResetButton onReset={resetFilters} />
          <div className="ml-auto flex items-center gap-2">
            <EcmExportButton
              onExport={() => {
                exportEcmDataset({
                  columns: ECM_PROVIDER_MEDICAL_COLUMNS,
                  fileName: 'ecm-provider-medical-demographics',
                  rows: items,
                });
              }}
            />
            {fieldSettingsControl}
          </div>
        </div>
      }
    >
      <Table className="min-w-[1700px]">
        <TableHeader className="bg-slate-50">
          <TableRow className="hover:bg-slate-50">
            {visibleColumnSet.has('firstName') ? <TableHead><SortableHeader field="firstName" label="First Name" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('lastName') ? <TableHead><SortableHeader field="lastName" label="Last Name" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('odrparticipant') ? <TableHead>ODR Participant Number</TableHead> : null}
            {visibleColumnSet.has('notes') ? <TableHead>Notes</TableHead> : null}
            {visibleColumnSet.has('cid') ? <TableHead><SortableHeader field="cid" label="CID" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('ssn') ? <TableHead>SSN</TableHead> : null}
            {visibleColumnSet.has('cin') ? <TableHead><SortableHeader field="cin" label="MediCal CIN" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('dob') ? <TableHead><SortableHeader field="dob" label="DOB" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('mrun') ? <TableHead><SortableHeader field="mrun" label="MRUN" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('fin') ? <TableHead>FIN</TableHead> : null}
            {visibleColumnSet.has('healthPlan') ? <TableHead><SortableHeader field="healthPlan" label="Health Plan Provider" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('qa') ? <TableHead><SortableHeader field="qa" label="QA" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
            {visibleColumnSet.has('lcm') ? <TableHead><SortableHeader field="lcm" label="LCM" onSort={handleSort} sortDirection={sortDirection} sortField={sortField} /></TableHead> : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleItems.length === 0 ? (
            <TableRow className="hover:bg-white">
              <TableCell className="py-10 text-center text-sm text-slate-500" colSpan={visibleColumnCount}>
                No participants match the current filters.
              </TableCell>
            </TableRow>
          ) : null}
          {visibleItems.map((item: any) => (
            <TableRow
              aria-haspopup="dialog"
              aria-label={`View Provider Medi-Cal Demographics details for ${getParticipantRecordLabel(item)}`}
              className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--azure-focus-ring)]"
              key={`${item.odrparticipant}-${item.participantId || 'none'}`}
              tabIndex={0}
              onClick={(event) => {
                if (!isInteractiveRecordTarget(event.target)) {
                  setSelectedDetailsItem(item);
                }
              }}
              onKeyDown={(event) => {
                if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) {
                  return;
                }
                event.preventDefault();
                setSelectedDetailsItem(item);
              }}
            >
              {visibleColumnSet.has('firstName') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.firstName) || '—'}</TableCell> : null}
              {visibleColumnSet.has('lastName') ? <TableCell className="whitespace-nowrap font-medium text-slate-900">{normalizeValue(item.lastName) || '—'}</TableCell> : null}
              {visibleColumnSet.has('odrparticipant') ? <TableCell className="whitespace-nowrap">
                <ParticipantProfileCellValue item={item} value={item.odrparticipant} />
              </TableCell> : null}
              {visibleColumnSet.has('notes') ? <TableCell className="max-w-[14rem]"><TextCell value={item.notes} /></TableCell> : null}
              {visibleColumnSet.has('cid') ? <TableCell className="whitespace-nowrap"><ParticipantProfileCellValue fallback={<MissingValue />} item={item} value={item.cid} /></TableCell> : null}
              {visibleColumnSet.has('ssn') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.ssn) || <MissingValue />}</TableCell> : null}
              {visibleColumnSet.has('cin') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.cin) || <MissingValue />}</TableCell> : null}
              {visibleColumnSet.has('dob') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.dob) ? formatDate(item.dob) : <MissingValue />}</TableCell> : null}
              {visibleColumnSet.has('mrun') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.mrun) || <MissingValue />}</TableCell> : null}
              {visibleColumnSet.has('fin') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.fin) || <MissingValue />}</TableCell> : null}
              {visibleColumnSet.has('healthPlan') ? <TableCell className="max-w-[12rem]">{normalizeValue(item.healthPlan) || <MissingValue />}</TableCell> : null}
              {visibleColumnSet.has('qa') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.qa) ? normalizeValue(item.qa).split(' ')[0] : <MissingValue />}</TableCell> : null}
              {visibleColumnSet.has('lcm') ? <TableCell className="max-w-[12rem]">{normalizeValue(item.lcm) || <MissingValue />}</TableCell> : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </PanelFrame>
    <EcmRecordDetailsDialog
      columns={ECM_PROVIDER_MEDICAL_COLUMNS}
      item={selectedDetailsItem}
      onOpenChange={(open) => {
        if (!open) {
          setSelectedDetailsItem(null);
        }
      }}
      title="Provider Medi-Cal Demographics Details"
    />
    </>
  );
};

export const EcmDashboardMifView = () => {
  const [selectedUploadId, setSelectedUploadId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [selectedDetailsItem, setSelectedDetailsItem] = useState<any | null>(null);
  const { fieldSettingsControl, visibleColumnCount, visibleColumnSet } = useEcmFieldSettings('mif', ECM_MIF_COLUMNS);

  const { data, error, loading } = useQuery(MIF_QUERY, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      mifId: selectedUploadId,
    },
  });

  const uploads = Array.isArray(data?.ecmMifTools?.uploads) ? data.ecmMifTools.uploads : [];
  const resolvedUploadId = data?.ecmMifTools?.selectedUploadId ?? null;
  const items = Array.isArray(data?.ecmMifTools?.items) ? data.ecmMifTools.items : [];

  useEffect(() => {
    if (selectedUploadId) {
      return;
    }

    if (!resolvedUploadId) {
      return;
    }

    setSelectedUploadId(resolvedUploadId);
  }, [resolvedUploadId, selectedUploadId]);

  let filteredItems = items.filter((item: any) => {
    const normalizedSearchText = normalizeLowerCase(searchText);
    if (!normalizedSearchText) {
      return true;
    }

    return buildSearchText([
      item.cid,
      item.cin,
      item.courtConsent,
      item.disposition,
      item.discontinuationReason,
      item.ecmCareManager,
      item.ecmOptIn,
      item.ecmProvider,
      item.ecmRecord,
      item.empaneledToClinic,
      item.empaneledToDhs,
      item.fin,
      item.firstName,
      item.gender,
      item.healthPlan,
      item.lastName,
      item.mrun,
      item.pcpName,
      item.ssn,
    ]).includes(normalizedSearchText);
  });

  const totalCount = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    if (page <= totalPages) {
      return;
    }

    setPage(totalPages);
  }, [page, totalPages]);

  // Pagination is temporarily disabled.
  // const visibleItems = filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const visibleItems = filteredItems;

  if (loading && !data) {
    return <OdrLoadingSpinner message="Loading ECM MiF Tools" />;
  }

  if (error && !data?.ecmMifTools) {
    return <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">Unable to load ECM MiF Tools: {error.message}</div>;
  }

  return (
    <>
    <PanelFrame
      error={error ? `Showing saved results while the latest refresh failed: ${error.message}` : null}
      summary={
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-slate-800">{totalCount} matching rows</span>
            <span>•</span>
            <span>{uploads.length} uploads</span>
          </div>
          {/* <PagerControls page={page} totalCount={totalCount} totalPages={totalPages} onPageChange={setPage} /> */}
        </>
      }
      toolbar={
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <FilterLabel>Upload</FilterLabel>
            <Select
              value={selectedUploadId ? `${selectedUploadId}` : ''}
              onValueChange={(value) => {
                setSelectedUploadId(Number(value));
                setPage(1);
              }}
            >
              <SelectTrigger className="h-8 w-[280px] text-xs">
                <SelectValue aria-label="Upload" />
              </SelectTrigger>
              <SelectContent>
                {uploads.map((upload: any) => (
                  <SelectItem key={upload.id} value={`${upload.id}`}>
                    {upload.title} {upload.createdAt ? `• ${formatDate(upload.createdAt)}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <FilterLabel>Search</FilterLabel>
            <Input
              className="h-8 w-[240px] text-xs"
              placeholder="Name, CID, CIN, provider"
              value={searchText}
              onChange={(event) => {
                setSearchText(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <EcmResetButton
            onReset={() => {
              setSearchText('');
              setPage(1);
            }}
          />
          <div className="ml-auto flex items-center gap-2">
            <EcmExportButton
              onExport={() => {
                exportEcmDataset({
                  columns: ECM_MIF_COLUMNS,
                  fileName: `ecm-mif-${selectedUploadId || 'latest'}`,
                  rows: items,
                });
              }}
            />
            {fieldSettingsControl}
          </div>
        </div>
      }
    >
      <Table className="min-w-[3200px]">
        <TableHeader className="bg-slate-50">
          <TableRow className="hover:bg-slate-50">
            {visibleColumnSet.has('odrparticipant') ? <TableHead>ODR Participant</TableHead> : null}
            {visibleColumnSet.has('cin') ? <TableHead>CIN</TableHead> : null}
            {visibleColumnSet.has('healthPlan') ? <TableHead>Health Plan Provider</TableHead> : null}
            {visibleColumnSet.has('cid') ? <TableHead>CID</TableHead> : null}
            {visibleColumnSet.has('firstName') ? <TableHead>First Name</TableHead> : null}
            {visibleColumnSet.has('lastName') ? <TableHead>Last Name</TableHead> : null}
            {visibleColumnSet.has('dob') ? <TableHead>DOB</TableHead> : null}
            {visibleColumnSet.has('gender') ? <TableHead>Gender</TableHead> : null}
            {visibleColumnSet.has('ssn') ? <TableHead>SSN</TableHead> : null}
            {visibleColumnSet.has('mrun') ? <TableHead>MRUN</TableHead> : null}
            {visibleColumnSet.has('fin') ? <TableHead>FIN</TableHead> : null}
            {visibleColumnSet.has('wpcGrandfathered') ? <TableHead>WPC Grandfathered</TableHead> : null}
            {visibleColumnSet.has('ecmProvider') ? <TableHead>ECM Provider</TableHead> : null}
            {visibleColumnSet.has('ecmCareManager') ? <TableHead>ECM Care Manager</TableHead> : null}
            {visibleColumnSet.has('ecmOptIn') ? <TableHead>ECM opt-in</TableHead> : null}
            {visibleColumnSet.has('ecmRecord') ? <TableHead>ECM Record</TableHead> : null}
            {visibleColumnSet.has('ecmEnrollmentStartDate') ? <TableHead>ECM Enrollment Start Date</TableHead> : null}
            {visibleColumnSet.has('ecmEnrollmentEndDate') ? <TableHead>ECM Enrollment End Date</TableHead> : null}
            {visibleColumnSet.has('ecmEffectiveDate') ? <TableHead>ECM Effective Date</TableHead> : null}
            {visibleColumnSet.has('ecmReferralDate') ? <TableHead>ECM Referral Date</TableHead> : null}
            {visibleColumnSet.has('discontinuationReason') ? <TableHead>Discontinuation Reason Code</TableHead> : null}
            {visibleColumnSet.has('empanelmentEffectiveDate') ? <TableHead>Empanelment Effective Date</TableHead> : null}
            {visibleColumnSet.has('empanelmentRequestedDate') ? <TableHead>Empanelment Requested Date</TableHead> : null}
            {visibleColumnSet.has('empaneledToDhs') ? <TableHead>Empaneled to DHS</TableHead> : null}
            {visibleColumnSet.has('empaneledToClinic') ? <TableHead>Empaneled to Clinic</TableHead> : null}
            {visibleColumnSet.has('pcpName') ? <TableHead>PCP Name</TableHead> : null}
            {visibleColumnSet.has('courtConsent') ? <TableHead>Court Consent</TableHead> : null}
            {visibleColumnSet.has('disposition') ? <TableHead>Disposition</TableHead> : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleItems.length === 0 ? (
            <TableRow className="hover:bg-white">
              <TableCell className="py-10 text-center text-sm text-slate-500" colSpan={visibleColumnCount}>
                No MiF rows match the current filters.
              </TableCell>
            </TableRow>
          ) : null}
          {visibleItems.map((item: any, index: number) => (
            <TableRow
              aria-haspopup="dialog"
              aria-label={`View MiF details for ${getParticipantRecordLabel(item)}`}
              className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--azure-focus-ring)]"
              key={`${item.odrparticipant || 'none'}-${item.participantId || index}`}
              tabIndex={0}
              onClick={(event) => {
                if (!isInteractiveRecordTarget(event.target)) {
                  setSelectedDetailsItem(item);
                }
              }}
              onKeyDown={(event) => {
                if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) {
                  return;
                }
                event.preventDefault();
                setSelectedDetailsItem(item);
              }}
            >
              {visibleColumnSet.has('odrparticipant') ? <TableCell className="whitespace-nowrap">{item.odrparticipant || '—'}</TableCell> : null}
              {visibleColumnSet.has('cin') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.cin) || '—'}</TableCell> : null}
              {visibleColumnSet.has('healthPlan') ? <TableCell className="max-w-[12rem]"><TextCell value={item.healthPlan} /></TableCell> : null}
              {visibleColumnSet.has('cid') ? <TableCell className="whitespace-nowrap"><ParticipantProfileCellValue item={item} value={item.cid} /></TableCell> : null}
              {visibleColumnSet.has('firstName') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.firstName) || '—'}</TableCell> : null}
              {visibleColumnSet.has('lastName') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.lastName) || '—'}</TableCell> : null}
              {visibleColumnSet.has('dob') ? <TableCell className="whitespace-nowrap">{formatDate(item.dob)}</TableCell> : null}
              {visibleColumnSet.has('gender') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.gender) || '—'}</TableCell> : null}
              {visibleColumnSet.has('ssn') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.ssn) || '—'}</TableCell> : null}
              {visibleColumnSet.has('mrun') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.mrun) || '—'}</TableCell> : null}
              {visibleColumnSet.has('fin') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.fin) || '—'}</TableCell> : null}
              {visibleColumnSet.has('wpcGrandfathered') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.wpcGrandfathered) || '—'}</TableCell> : null}
              {visibleColumnSet.has('ecmProvider') ? <TableCell className="max-w-[12rem]"><TextCell value={item.ecmProvider} /></TableCell> : null}
              {visibleColumnSet.has('ecmCareManager') ? <TableCell className="max-w-[12rem]"><TextCell value={item.ecmCareManager} /></TableCell> : null}
              {visibleColumnSet.has('ecmOptIn') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.ecmOptIn) || '—'}</TableCell> : null}
              {visibleColumnSet.has('ecmRecord') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.ecmRecord) || '—'}</TableCell> : null}
              {visibleColumnSet.has('ecmEnrollmentStartDate') ? <TableCell className="whitespace-nowrap">{formatDate(item.ecmEnrollmentStartDate)}</TableCell> : null}
              {visibleColumnSet.has('ecmEnrollmentEndDate') ? <TableCell className="whitespace-nowrap">{formatDate(item.ecmEnrollmentEndDate)}</TableCell> : null}
              {visibleColumnSet.has('ecmEffectiveDate') ? <TableCell className="whitespace-nowrap">{formatDate(item.ecmEffectiveDate)}</TableCell> : null}
              {visibleColumnSet.has('ecmReferralDate') ? <TableCell className="whitespace-nowrap">{formatDate(item.ecmReferralDate)}</TableCell> : null}
              {visibleColumnSet.has('discontinuationReason') ? <TableCell className="max-w-[12rem]"><TextCell value={item.discontinuationReason} /></TableCell> : null}
              {visibleColumnSet.has('empanelmentEffectiveDate') ? <TableCell className="whitespace-nowrap">{formatDate(item.empanelmentEffectiveDate)}</TableCell> : null}
              {visibleColumnSet.has('empanelmentRequestedDate') ? <TableCell className="whitespace-nowrap">{formatDate(item.empanelmentRequestedDate)}</TableCell> : null}
              {visibleColumnSet.has('empaneledToDhs') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.empaneledToDhs) || '—'}</TableCell> : null}
              {visibleColumnSet.has('empaneledToClinic') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.empaneledToClinic) || '—'}</TableCell> : null}
              {visibleColumnSet.has('pcpName') ? <TableCell className="max-w-[12rem]"><TextCell value={item.pcpName} /></TableCell> : null}
              {visibleColumnSet.has('courtConsent') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.courtConsent) || '—'}</TableCell> : null}
              {visibleColumnSet.has('disposition') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.disposition) || '—'}</TableCell> : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </PanelFrame>
    <EcmRecordDetailsDialog
      columns={ECM_MIF_COLUMNS}
      item={selectedDetailsItem}
      onOpenChange={(open) => {
        if (!open) {
          setSelectedDetailsItem(null);
        }
      }}
      title="MiF Details"
    />
    </>
  );
};

export const EcmDashboardCensusLockView = ({ canLockCensus = false }: { canLockCensus?: boolean }) => {
  const [searchText, setSearchText] = useState('');
  const [programFilter, setProgramFilter] = useState(ALL_FILTER_VALUE);
  const [facilityFilter, setFacilityFilter] = useState(ALL_FILTER_VALUE);
  const [qaFilter, setQaFilter] = useState(ALL_FILTER_VALUE);
  const [lcmFilter, setLcmFilter] = useState(ALL_FILTER_VALUE);
  const [page, setPage] = useState(1);
  const [selectedMonthKey, setSelectedMonthKey] = useState(getCurrentMonthKey);
  const [selectedDetailsItem, setSelectedDetailsItem] = useState<any | null>(null);
  const { fieldSettingsControl, visibleColumnCount, visibleColumnSet } = useEcmFieldSettings('census-lock', ECM_CENSUS_LOCK_COLUMNS);

  const selectedMonth = parseMonthKey(selectedMonthKey);
  const { data, error, loading, refetch } = useQuery(CENSUS_LOCK_QUERY, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: selectedMonth,
  });
  const [lockCensusMonth, { loading: lockLoading }] = useMutation(LOCK_CENSUS_MONTH_MUTATION);

  const result = data?.ecmCensusLock;
  const monthOptions = buildMonthOptions(result?.dateRangeStart, result?.dateRangeEnd);
  const selectedMonthLabel = getSelectedMonthLabel(selectedMonthKey, monthOptions);
  const viewMode = result?.viewMode || 'live';
  const statusLabel = viewMode === 'locked' ? 'Locked Month' : viewMode === 'future' ? 'Future Month' : 'Unlocked Month';
  const canShowLockButton = Boolean(canLockCensus && result && viewMode !== 'locked' && viewMode !== 'future');

  const handleLockCensus = async () => {
    const confirmed = window.confirm(`Lock the ECM census for ${selectedMonthLabel}? This will freeze the current DD2 census rows for the selected month.`);
    if (!confirmed) {
      return;
    }

    try {
      const mutationResult = await lockCensusMonth({
        variables: selectedMonth,
      });
      const lockResult = mutationResult.data?.ecmLockCensusMonth;
      const participantCount = Number(lockResult?.participantsLocked || 0);
      toast.success(lockResult?.alreadyLocked
        ? `${selectedMonthLabel} is already locked.`
        : `Locked ${participantCount} census ${participantCount === 1 ? 'row' : 'rows'} for ${selectedMonthLabel}.`);
      await refetch();
    } catch (mutationError: any) {
      toast.error(mutationError?.message || 'Unable to lock ECM census month');
    }
  };

  useEffect(() => {
    if (monthOptions.length === 0) {
      return;
    }

    const hasSelectedMonth = monthOptions.some((option) => option.value === selectedMonthKey);
    if (hasSelectedMonth) {
      return;
    }

    setSelectedMonthKey(monthOptions[0].value);
    setPage(1);
  }, [monthOptions, selectedMonthKey]);

  const items = Array.isArray(result?.items) ? result.items : [];
  const programs = uniqueSortedValues(items.map((item: any) => item.program));
  const facilities = uniqueSortedValues(items.map((item: any) => item.facility));
  const qas = uniqueSortedValues(items.map((item: any) => item.qa));
  const lcms = uniqueSortedValues(items.map((item: any) => item.lcm));

  const filteredItems = items.filter((item: any) => {
    if (!matchesFilter(item.program, programFilter)) {
      return false;
    }

    if (!matchesFilter(item.facility, facilityFilter)) {
      return false;
    }

    if (!matchesFilter(item.qa, qaFilter)) {
      return false;
    }

    if (!matchesFilter(item.lcm, lcmFilter)) {
      return false;
    }

    const normalizedSearchText = normalizeLowerCase(searchText);
    if (!normalizedSearchText) {
      return true;
    }

    return buildSearchText([
      item.address,
      item.cid,
      item.cin,
      item.facility,
      item.firstName,
      item.healthPlan,
      item.lastName,
      item.lcm,
      item.notes,
      item.phoneNumber,
      item.program,
      item.qa,
    ]).includes(normalizedSearchText);
  });

  const totalCount = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    if (page <= totalPages) {
      return;
    }

    setPage(totalPages);
  }, [page, totalPages]);

  // Pagination is temporarily disabled.
  // const visibleItems = filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const visibleItems = filteredItems;

  const resetFilters = () => {
    setSearchText('');
    setProgramFilter(ALL_FILTER_VALUE);
    setFacilityFilter(ALL_FILTER_VALUE);
    setQaFilter(ALL_FILTER_VALUE);
    setLcmFilter(ALL_FILTER_VALUE);
    setPage(1);
  };

  if (loading && !data) {
    return <OdrLoadingSpinner message="Loading ECM Census Lock" />;
  }

  if (error && !result) {
    return <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">Unable to load ECM Census Lock: {error.message}</div>;
  }

  if (result?.viewMode === 'future') {
    return (
      <div className="rounded-lg border border-slate-200 bg-white px-4 py-8 text-center shadow-sm">
        <div className="text-sm font-medium text-slate-900">No census is available for {selectedMonthLabel} yet.</div>
        <div className="mt-1 text-sm text-slate-600">Select the current month or a past month to review census history.</div>
      </div>
    );
  }

  return (
    <>
    <PanelFrame
      error={error ? `Showing saved results while the latest refresh failed: ${error.message}` : null}
      summary={
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-slate-800">{selectedMonthLabel}</span>
            <span>•</span>
            <span>{statusLabel}</span>
            {result?.lockDate ? (
              <>
                <span>•</span>
                <span>Lock date {formatDate(result.lockDate)}</span>
              </>
            ) : null}
          </div>
          {/* <PagerControls page={page} totalCount={totalCount} totalPages={totalPages} onPageChange={setPage} /> */}
        </>
      }
      toolbar={
        <div className="flex flex-wrap items-end gap-3">
          <MonthSelectControl monthOptions={monthOptions} selectedMonthKey={selectedMonthKey} selectedMonthLabel={selectedMonthLabel} onChange={setSelectedMonthKey} />
          <div className="space-y-1">
            <FilterLabel>Search</FilterLabel>
            <Input
              className="h-8 w-[240px] text-xs"
              placeholder="Name, CID, CIN"
              value={searchText}
              onChange={(event) => {
                setSearchText(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <SimpleSelect label="Program" value={programFilter} options={programs} allLabel="All Programs" onChange={setProgramFilter} />
          <SimpleSelect label="Facility" value={facilityFilter} options={facilities} allLabel="All Facilities" onChange={setFacilityFilter} />
          <SimpleSelect label="QA" value={qaFilter} options={qas} allLabel="All QA" onChange={setQaFilter} />
          <SimpleSelect label="LCM" value={lcmFilter} options={lcms} allLabel="All LCM" onChange={setLcmFilter} />
          <EcmResetButton onReset={resetFilters} />
          <div className="ml-auto flex items-center gap-2">
            {canShowLockButton ? (
              <Button
                className="h-8"
                disabled={lockLoading || loading}
                onClick={handleLockCensus}
                size="sm"
              >
                <LockKeyhole className="mr-2 h-4 w-4" />
                {lockLoading ? 'Locking' : 'Lock Census'}
              </Button>
            ) : null}
            <EcmExportButton
              onExport={() => {
                exportEcmDataset({
                  columns: ECM_CENSUS_LOCK_COLUMNS,
                  fileName: `ecm-census-lock-${selectedMonthKey}`,
                  rows: items,
                });
              }}
            />
            {fieldSettingsControl}
          </div>
        </div>
      }
    >
      <Table className="min-w-[1900px]">
        <TableHeader className="bg-slate-50">
          <TableRow className="hover:bg-slate-50">
            {visibleColumnSet.has('firstName') ? <TableHead>First Name</TableHead> : null}
            {visibleColumnSet.has('lastName') ? <TableHead>Last Name</TableHead> : null}
            {visibleColumnSet.has('program') ? <TableHead>Program</TableHead> : null}
            {visibleColumnSet.has('censusStart') ? <TableHead>Date Entered</TableHead> : null}
            {visibleColumnSet.has('authorizationEndDate') ? <TableHead>Auth End Date</TableHead> : null}
            {visibleColumnSet.has('address') ? <TableHead>Address</TableHead> : null}
            {visibleColumnSet.has('phoneNumber') ? <TableHead>Phone #</TableHead> : null}
            {visibleColumnSet.has('notes') ? <TableHead>Notes</TableHead> : null}
            {visibleColumnSet.has('cid') ? <TableHead>CID</TableHead> : null}
            {visibleColumnSet.has('cin') ? <TableHead>CIN</TableHead> : null}
            {visibleColumnSet.has('healthPlan') ? <TableHead>Health Plan Provider</TableHead> : null}
            {visibleColumnSet.has('facility') ? <TableHead>Facility</TableHead> : null}
            {visibleColumnSet.has('lcm') ? <TableHead>LCM</TableHead> : null}
            {visibleColumnSet.has('dob') ? <TableHead>DOB</TableHead> : null}
            {visibleColumnSet.has('qa') ? <TableHead>QA</TableHead> : null}
            {visibleColumnSet.has('status') ? <TableHead>Status</TableHead> : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleItems.length === 0 ? (
            <TableRow className="hover:bg-white">
              <TableCell className="py-10 text-center text-sm text-slate-500" colSpan={visibleColumnCount}>
                No participants match the current filters.
              </TableCell>
            </TableRow>
          ) : null}
          {visibleItems.map((item: any) => (
            <TableRow
              aria-haspopup="dialog"
              aria-label={`View Census Lock details for ${getParticipantRecordLabel(item)}`}
              className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--azure-focus-ring)]"
              key={`${item.odrparticipant}-${item.participantId || 'none'}`}
              tabIndex={0}
              onClick={(event) => {
                if (!isInteractiveRecordTarget(event.target)) {
                  setSelectedDetailsItem(item);
                }
              }}
              onKeyDown={(event) => {
                if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) {
                  return;
                }
                event.preventDefault();
                setSelectedDetailsItem(item);
              }}
            >
              {visibleColumnSet.has('firstName') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.firstName) || '—'}</TableCell> : null}
              {visibleColumnSet.has('lastName') ? <TableCell className="whitespace-nowrap font-medium text-slate-900">{normalizeValue(item.lastName) || '—'}</TableCell> : null}
              {visibleColumnSet.has('program') ? (
                <TableCell className="whitespace-nowrap"><ParticipantProfileCellValue item={item} value={item.program} /></TableCell>
              ) : null}
              {visibleColumnSet.has('censusStart') ? <TableCell className="whitespace-nowrap">{formatDate(item.censusStart)}</TableCell> : null}
              {visibleColumnSet.has('authorizationEndDate') ? <TableCell className="whitespace-nowrap">{formatDate(item.authorizationEndDate)}</TableCell> : null}
              {visibleColumnSet.has('address') ? <TableCell className="max-w-[14rem]"><TextCell value={item.address} /></TableCell> : null}
              {visibleColumnSet.has('phoneNumber') ? <TableCell className="whitespace-nowrap"><TextCell value={item.phoneNumber} /></TableCell> : null}
              {visibleColumnSet.has('notes') ? <TableCell className="max-w-[14rem]"><TextCell value={item.notes} /></TableCell> : null}
              {visibleColumnSet.has('cid') ? <TableCell className="whitespace-nowrap"><ParticipantProfileCellValue item={item} value={item.cid} /></TableCell> : null}
              {visibleColumnSet.has('cin') ? <TableCell className="whitespace-nowrap">{normalizeValue(item.cin) || '—'}</TableCell> : null}
              {visibleColumnSet.has('healthPlan') ? <TableCell className="max-w-[12rem]"><TextCell value={item.healthPlan} /></TableCell> : null}
              {visibleColumnSet.has('facility') ? <TableCell className="max-w-[12rem]"><TextCell value={item.facility} /></TableCell> : null}
              {visibleColumnSet.has('lcm') ? <TableCell className="max-w-[12rem]"><TextCell value={item.lcm} /></TableCell> : null}
              {visibleColumnSet.has('dob') ? <TableCell className="whitespace-nowrap">{formatDate(item.dob)}</TableCell> : null}
              {visibleColumnSet.has('qa') ? <TableCell className="max-w-[10rem]"><TextCell value={item.qa} /></TableCell> : null}
              {visibleColumnSet.has('status') ? <TableCell className="whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <StatusIcon status={result?.viewMode === 'locked' ? 'locked' : 'pending'} />
                  <span>{result?.viewMode === 'locked' ? 'Locked' : 'Live'}</span>
                </div>
              </TableCell> : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </PanelFrame>
    <EcmRecordDetailsDialog
      columns={ECM_CENSUS_LOCK_COLUMNS}
      item={selectedDetailsItem}
      onOpenChange={(open) => {
        if (!open) {
          setSelectedDetailsItem(null);
        }
      }}
      title="Census Lock Details"
    />
    </>
  );
};

export const EcmDashboardLegacyDashboardView = () => {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-slate-900">Legacy ECM Dashboard</div>
          <div className="text-sm text-slate-600">Open the classic dashboard tools in the legacy workspace.</div>
        </div>
        <a
          className="inline-flex h-8 items-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 hover:border-slate-400 hover:text-slate-900"
          href="/app/components/ecm/legacy_dashboard"
        >
          Open Legacy Dashboard
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    </div>
  );
};
